import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  ArrowUpRight,
  Ban,
  Building2,
  CheckCircle,
  Clock,
  Copy,
  Crown,
  KeyRound,
  LayoutDashboard,
  Lock,
  Mail,
  MessageSquareText,
  Plus,
  RefreshCw,
  Send,
  ShieldCheck,
  Sparkles,
  Ticket,
  Trash2,
  UserCog,
  Users,
  WalletCards,
  X,
} from 'lucide-react';
import { Button } from '../components/Button';
import { useStore } from '../context/StoreContext';
import { api } from '../services/api';
import { AdminTeamMessage, BillingConfig, BillingPlanKey, Company, InviteCode, SubscriptionNoticeRecurrence, SubscriptionStatus, User, UserRole } from '../types';

type AdminTab = 'dashboard' | 'companies' | 'admins' | 'emails' | 'plans';
type CommunicationPreviewType =
  | 'MONTHLY_REPORT_PREVIEW'
  | 'SUBSCRIPTION_EXPIRING_PREVIEW'
  | 'SUBSCRIPTION_EXPIRED_PREVIEW'
  | 'ACCOUNT_VERIFICATION_PREVIEW'
  | 'PASSWORD_CHANGED_PREVIEW'
  | 'BILLING_PREVIEW'
  | 'MAINTENANCE_PREVIEW'
  | 'PRODUCT_NEWS_PREVIEW';

const communicationPreviewOptions: Array<{ value: CommunicationPreviewType; label: string }> = [
  { value: 'MONTHLY_REPORT_PREVIEW', label: 'Relatório mensal' },
  { value: 'SUBSCRIPTION_EXPIRING_PREVIEW', label: 'Assinatura vencendo' },
  { value: 'SUBSCRIPTION_EXPIRED_PREVIEW', label: 'Assinatura vencida' },
  { value: 'ACCOUNT_VERIFICATION_PREVIEW', label: 'Validação de conta' },
  { value: 'PASSWORD_CHANGED_PREVIEW', label: 'Senha alterada' },
  { value: 'BILLING_PREVIEW', label: 'Cobrança/Fatura' },
  { value: 'MAINTENANCE_PREVIEW', label: 'Manutenção' },
  { value: 'PRODUCT_NEWS_PREVIEW', label: 'Novidades' },
];

const subscriptionNoticeRecurrenceOptions: Array<{ value: SubscriptionNoticeRecurrence; label: string; description: string }> = [
  { value: 'ONCE_PER_CYCLE', label: 'Uma vez por vencimento', description: 'Envia apenas um aviso depois que a assinatura vencer.' },
  { value: 'MILESTONES', label: '7, 3 e 1 dias + vencida', description: 'Lembretes nos marcos principais e um aviso após o vencimento.' },
  { value: 'WEEKLY_AFTER_EXPIRY', label: 'Semanal após vencer', description: 'Mantém os marcos e repete no máximo uma vez por semana após vencer.' },
  { value: 'PROGRESSIVE_AFTER_EXPIRY', label: 'Progressiva: semanal, quinzenal, mensal e trimestral', description: 'Após vencer, reduz a frequência: semanal no 1º mês, quinzenal no 2º, mensal no 3º e trimestral depois.' },
  { value: 'DISABLED', label: 'Desativar automático', description: 'Não envia avisos automáticos de assinatura.' },
];

const planPrice: Record<Company['plan'], number> = {
  BASIC: 79.9,
  PRO: 129.9,
  ENTERPRISE: 199.9,
};

const formatCurrency = (value: number) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatDate = (date?: string) => {
  if (!date) return 'Sem data';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return 'Data inválida';
  return parsed.toLocaleDateString('pt-BR');
};

const formatDateForInput = (date?: string) => {
  if (!date) return '';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toISOString().split('T')[0];
};

const getDateAfterDaysForInput = (days: number) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().split('T')[0];
};

const companyPlanKeys: BillingPlanKey[] = ['BASIC', 'PRO', 'ENTERPRISE'];

const getCompanyMonthlyPrice = (company: Company) => {
  const override = Number(company.monthlyPriceOverride);
  if (Number.isFinite(override) && override >= 1) return override;
  return planPrice[company.plan] || planPrice.BASIC;
};

const parseCurrencyInput = (value: string) => {
  const cleaned = value.trim().replace(/[^\d,.]/g, '');
  if (!cleaned) return null;

  const lastComma = cleaned.lastIndexOf(',');
  const lastDot = cleaned.lastIndexOf('.');
  let normalized = cleaned;

  if (lastComma >= 0 && lastDot >= 0) {
    normalized = lastComma > lastDot
      ? cleaned.replace(/\./g, '').replace(',', '.')
      : cleaned.replace(/,/g, '');
  } else if (lastComma >= 0) {
    normalized = cleaned.replace(',', '.');
  } else if (lastDot >= 0) {
    const [, cents = ''] = cleaned.split('.');
    normalized = cents.length === 3 ? cleaned.replace(/\./g, '') : cleaned;
  }

  return Number(normalized);
};

const getStatusLabel = (status: SubscriptionStatus | string) => {
  switch (status) {
    case SubscriptionStatus.ACTIVE:
      return 'Ativo';
    case SubscriptionStatus.TRIAL:
      return 'Trial';
    case SubscriptionStatus.PENDING:
      return 'Pausada';
    case SubscriptionStatus.CANCELED:
      return 'Bloqueado';
    default:
      return status;
  }
};

const getStatusClass = (status: SubscriptionStatus | string) => {
  switch (status) {
    case SubscriptionStatus.ACTIVE:
      return 'bg-emerald-100 text-emerald-700 ring-emerald-200';
    case SubscriptionStatus.TRIAL:
      return 'bg-sky-100 text-sky-700 ring-sky-200';
    case SubscriptionStatus.PENDING:
      return 'bg-amber-100 text-amber-700 ring-amber-200';
    case SubscriptionStatus.CANCELED:
      return 'bg-rose-100 text-rose-700 ring-rose-200';
    default:
      return 'bg-slate-100 text-slate-700 ring-slate-200';
  }
};

const getAdminLabel = (level?: 0 | 1 | 2) => {
  if (level === 0) return 'Super Admin';
  if (level === 1) return 'Gerente';
  return 'Aprovador';
};

const getAdminDescription = (level?: 0 | 1 | 2) => {
  if (level === 0) return 'Acesso total, equipe e exclusões';
  if (level === 1) return 'Operação, cobrança e suporte';
  return 'Convites e acompanhamento';
};

const daysUntil = (date?: string) => {
  if (!date) return null;
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  parsed.setHours(0, 0, 0, 0);
  return Math.ceil((parsed.getTime() - today.getTime()) / 86400000);
};

const PanelCard: React.FC<{
  title: string;
  value: string | number;
  description: string;
  icon: React.ElementType;
  tone?: 'dark' | 'emerald' | 'amber' | 'rose' | 'sky';
}> = ({ title, value, description, icon: Icon, tone = 'dark' }) => {
  const tones = {
    dark: 'from-slate-950 to-slate-800 text-white',
    emerald: 'from-emerald-600 to-teal-500 text-white',
    amber: 'from-amber-500 to-orange-500 text-white',
    rose: 'from-rose-500 to-red-500 text-white',
    sky: 'from-sky-600 to-blue-500 text-white',
  };

  return (
    <div className={`relative overflow-hidden rounded-[1.75rem] bg-gradient-to-br ${tones[tone]} p-5 shadow-xl shadow-slate-200/70`}>
      <div className="absolute -right-8 -top-8 h-28 w-28 rounded-full bg-white/10" />
      <div className="relative flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-black uppercase tracking-[0.22em] opacity-75">{title}</p>
          <h3 className="mt-3 text-3xl font-black tracking-tight">{value}</h3>
          <p className="mt-2 text-sm font-semibold opacity-80">{description}</p>
        </div>
        <div className="rounded-2xl bg-white/15 p-3 ring-1 ring-white/20">
          <Icon className="h-6 w-6" />
        </div>
      </div>
    </div>
  );
};

export const AdminSubscription = () => {
  const {
    user,
    allCompanies,
    toggleCompanyStatus,
    updateCompanyBillingDate,
    deleteCompany,
    adminUpdatePassword,
    createAdmin,
    getAdmins,
    deleteAdmin,
    generateInviteCode,
    getActiveInvites,
    refreshData,
    deleteInviteCode,
  } = useStore();

  const [activeTab, setActiveTab] = useState<AdminTab>('dashboard');
  const [editingCompanyId, setEditingCompanyId] = useState<string | null>(null);
  const [editingCompanyName, setEditingCompanyName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [dateEditCompanyId, setDateEditCompanyId] = useState<string | null>(null);
  const [newBillingDate, setNewBillingDate] = useState('');
  const [loadingAction, setLoadingAction] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [admins, setAdmins] = useState<User[]>([]);
  const [activeInvites, setActiveInvites] = useState<InviteCode[]>([]);
  const [generatingCode, setGeneratingCode] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [newAdminName, setNewAdminName] = useState('');
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newAdminPass, setNewAdminPass] = useState('');
  const [newAdminLevel, setNewAdminLevel] = useState<0 | 1 | 2>(2);
  const [newAdminCanSendEmails, setNewAdminCanSendEmails] = useState(false);
  const [newAdminCanLeadTeam, setNewAdminCanLeadTeam] = useState(false);
  const [sendingPreviewEmails, setSendingPreviewEmails] = useState(false);
  const [selectedPreviewType, setSelectedPreviewType] = useState<CommunicationPreviewType>('MONTHLY_REPORT_PREVIEW');
  const [selectedEmailCompanyIds, setSelectedEmailCompanyIds] = useState<string[]>([]);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailExtraMessage, setEmailExtraMessage] = useState('');
  const [emailCtaLabel, setEmailCtaLabel] = useState('Acessar FlowTab');
  const [emailCtaUrl, setEmailCtaUrl] = useState('https://www.flowtab.com.br/');
  const [winbackExtraMessage, setWinbackExtraMessage] = useState('');
  const [sendingEmailCenter, setSendingEmailCenter] = useState(false);
  const [teamSearch, setTeamSearch] = useState('');
  const [teamMessages, setTeamMessages] = useState<AdminTeamMessage[]>([]);
  const [teamMessage, setTeamMessage] = useState('');
  const [teamPriority, setTeamPriority] = useState<'NORMAL' | 'IMPORTANT' | 'URGENT'>('NORMAL');
  const [teamTargetAdminId, setTeamTargetAdminId] = useState('');
  const [teamTargetCompanyId, setTeamTargetCompanyId] = useState('');
  const [savingTeam, setSavingTeam] = useState(false);
  const [showTeamMessageModal, setShowTeamMessageModal] = useState(false);
  const [selectedTeamAdminId, setSelectedTeamAdminId] = useState<string | null>(null);
  const teamCanvasRef = useRef<HTMLDivElement | null>(null);
  const [teamPanOffset, setTeamPanOffset] = useState({ x: 0, y: 0 });
  const teamPanRef = useRef({ active: false, x: 0, y: 0, startX: 0, startY: 0 });
  const adminAutoRefreshRef = useRef({ running: false, lastRunAt: 0 });
  const [billingConfig, setBillingConfig] = useState<BillingConfig | null>(null);
  const [savingPlan, setSavingPlan] = useState<BillingPlanKey | null>(null);
  const [savingNoticeRecurrence, setSavingNoticeRecurrence] = useState(false);
  const [savingTextractLimit, setSavingTextractLimit] = useState(false);

  const isSuperAdmin = user?.role === UserRole.SAAS_ADMIN && user.adminLevel === 0;
  const canManageBilling = user?.role === UserRole.SAAS_ADMIN && typeof user.adminLevel === 'number' && user.adminLevel < 2;
  const canDeleteCompanies = isSuperAdmin;
  const canManageAdmins = isSuperAdmin;
  const canViewTeam = user?.role === UserRole.SAAS_ADMIN;
  const canManageTeam = isSuperAdmin || Boolean(user?.canLeadTeam);
  const canUseEmailCenter = isSuperAdmin || Boolean(user?.canSendEmails);

  const refreshInvites = async () => {
    const invites = await getActiveInvites();
    setActiveInvites(invites || []);
  };

  const loadAdmins = async () => {
    const adminList = await getAdmins();
    setAdmins(adminList || []);
  };

  const loadTeamMessages = async () => {
    const messages = await api.adminTeam.getMessages();
    setTeamMessages(messages || []);
  };

  const loadBillingConfig = async () => {
    const config = await api.billing.getConfig();
    setBillingConfig(config);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshData();
      await refreshInvites();
      await loadBillingConfig();
      if (canViewTeam) {
        await loadAdmins();
        await loadTeamMessages();
      }
    } catch (error) {
      console.error('Erro ao atualizar dados do painel:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const isEditingAdminForm = () => {
    const activeElement = document.activeElement;
    if (!activeElement) return false;
    const tagName = activeElement.tagName.toLowerCase();
    return tagName === 'input' || tagName === 'textarea' || tagName === 'select' || activeElement.getAttribute('contenteditable') === 'true';
  };

  const handleAdminAutoRefresh = useCallback(async () => {
    const now = Date.now();
    if (document.hidden) return;
    if (refreshing || loadingAction || savingPlan || savingTeam || sendingEmailCenter || sendingPreviewEmails || generatingCode) return;
    if (isEditingAdminForm()) return;
    if (adminAutoRefreshRef.current.running) return;
    if (now - adminAutoRefreshRef.current.lastRunAt < 85000) return;

    adminAutoRefreshRef.current.running = true;
    try {
      await refreshData();
      await refreshInvites();
      await loadBillingConfig();
      if (canViewTeam) {
        await loadAdmins();
        await loadTeamMessages();
      }
      adminAutoRefreshRef.current.lastRunAt = Date.now();
    } catch (error) {
      console.error('Erro ao atualizar painel automaticamente:', error);
    } finally {
      adminAutoRefreshRef.current.running = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewTeam, refreshing, loadingAction, savingPlan, savingTeam, sendingEmailCenter, sendingPreviewEmails, generatingCode, refreshData]);

  const handleSendPreviewEmails = async () => {
    if (!canUseEmailCenter) return alert('Seu acesso não permite enviar e-mails.');
    const previewLabel = communicationPreviewOptions.find(option => option.value === selectedPreviewType)?.label || 'prévia';
    const targetEmail = user?.email || 'seu e-mail de administrador';
    if (!window.confirm(`Enviar prévia "${previewLabel}" da Central FlowTab para ${targetEmail}`)) return;

    setSendingPreviewEmails(true);
    try {
      const result = await api.communications.sendPreviewSuite(selectedPreviewType, 'FlowTab Prévia');
      if (result?.success) {
        const failedText = result.failed ? ` (${result.failed} falharam)` : '';
        alert(`Prévia "${previewLabel}" enviada para ${result.sentTo || targetEmail}.${failedText}`);
      } else {
        alert(result?.error || 'Não foi possível enviar a prévia agora.');
      }
    } catch (error) {
      alert('Erro ao enviar a prévia da Central FlowTab.');
    } finally {
      setSendingPreviewEmails(false);
    }
  };

  const toggleEmailCompany = (companyId: string) => {
    setSelectedEmailCompanyIds(prev =>
      prev.includes(companyId) ? prev.filter(id => id !== companyId) : [...prev, companyId]
    );
  };

  const selectPausedCompanies = () => {
    setSelectedEmailCompanyIds(
      companiesList
        .filter(company => [SubscriptionStatus.PENDING, SubscriptionStatus.CANCELED].includes(company.subscriptionStatus))
        .map(company => company.id)
    );
  };

  const getPreviewCompanyId = () => selectedEmailCompanyIds[0] || selectedEmailCompanies[0]?.id;

  const handlePreviewCustomEmail = async () => {
    if (!canUseEmailCenter) return alert('Seu acesso não permite enviar e-mails.');
    if (!emailSubject.trim()) return alert('Informe o assunto para gerar a prévia.');
    if (!emailMessage.trim()) return alert('Informe a mensagem principal para gerar a prévia.');

    setSendingEmailCenter(true);
    try {
      const result = await api.communications.previewCustomEmail({
        companyId: getPreviewCompanyId(),
        subject: emailSubject.trim(),
        title: emailSubject.trim(),
        message: emailMessage.trim(),
        extraMessage: emailExtraMessage.trim(),
        ctaLabel: emailCtaLabel.trim() || 'Acessar FlowTab',
        ctaUrl: emailCtaUrl.trim() || 'https://www.flowtab.com.br/'
      });
      alert(result.success ? `Prévia enviada para ${result.sentTo || user?.email}.` : (result?.error || 'Não foi possível enviar a prévia agora.'));
    } finally {
      setSendingEmailCenter(false);
    }
  };

  const handleSendCustomEmail = async () => {
    if (!canUseEmailCenter) return alert('Seu acesso não permite enviar e-mails.');
    if (!selectedEmailCompanyIds.length) return alert('Selecione pelo menos uma empresa.');
    if (!emailSubject.trim()) return alert('Informe o assunto.');
    if (!emailMessage.trim()) return alert('Informe a mensagem principal.');
    if (!window.confirm(`Enviar e-mail para ${selectedEmailCompanyIds.length} cliente(s) selecionado(s)`)) return;

    setSendingEmailCenter(true);
    try {
      const result = await api.communications.sendCustomEmail({
        companyIds: selectedEmailCompanyIds,
        subject: emailSubject.trim(),
        title: emailSubject.trim(),
        message: emailMessage.trim(),
        extraMessage: emailExtraMessage.trim(),
        ctaLabel: emailCtaLabel.trim() || 'Acessar FlowTab',
        ctaUrl: emailCtaUrl.trim() || 'https://www.flowtab.com.br/'
      });
      alert(result.success ? `Enviado: ${result.sent}. Falhas: ${result.failed}.` : (result?.error || 'Não foi possível enviar agora.'));
    } finally {
      setSendingEmailCenter(false);
    }
  };

  const handlePreviewWinbackEmail = async () => {
    if (!canUseEmailCenter) return alert('Seu acesso não permite enviar e-mails.');

    setSendingEmailCenter(true);
    try {
      const result = await api.communications.previewWinbackEmail({
        companyId: getPreviewCompanyId(),
        extraMessage: winbackExtraMessage.trim()
      });
      alert(result.success ? `Prévia de retorno enviada para ${result.sentTo || user?.email}.` : (result?.error || 'Não foi possível enviar a prévia agora.'));
    } finally {
      setSendingEmailCenter(false);
    }
  };

  const handleSendWinbackEmail = async () => {
    if (!canUseEmailCenter) return alert('Seu acesso não permite enviar e-mails.');
    if (!selectedEmailCompanyIds.length) return alert('Selecione os clientes que devem receber o convite de retorno.');
    if (!window.confirm(`Enviar campanha de retorno para ${selectedEmailCompanyIds.length} cliente(s)`)) return;

    setSendingEmailCenter(true);
    try {
      const result = await api.communications.sendWinbackEmail({
        companyIds: selectedEmailCompanyIds,
        extraMessage: winbackExtraMessage.trim()
      });
      alert(result.success ? `Retorno enviado: ${result.sent}. Falhas: ${result.failed}.` : (result?.error || 'Não foi possível enviar agora.'));
    } finally {
      setSendingEmailCenter(false);
    }
  };

  const updatePlanDraft = (plan: BillingPlanKey, updater: (current: NonNullable<BillingConfig['plans']>[number]) => NonNullable<BillingConfig['plans']>[number]) => {
    setBillingConfig(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        plans: prev.plans.map(item => item.plan === plan ? updater(item) : item)
      };
    });
  };

  const handleSavePlan = async (plan: BillingPlanKey) => {
    if (!isSuperAdmin) return alert('Apenas Super Admin pode alterar planos.');
    const planConfig = billingConfig?.plans.find(item => item.plan === plan);
    if (!planConfig) return;

    setSavingPlan(plan);
    try {
      const saved = await api.billing.updatePlan(plan, {
        price: Number(planConfig.price || 0),
        features: planConfig.features
      });
      if (!saved) return alert('Não foi possível salvar o plano agora.');
      updatePlanDraft(plan, () => saved);
      alert(`Plano ${plan} salvo com sucesso.`);
    } finally {
      setSavingPlan(null);
    }
  };

  const handleNoticeRecurrenceChange = async (value: SubscriptionNoticeRecurrence) => {
    if (!isSuperAdmin || !billingConfig || savingNoticeRecurrence) return;
    const previousValue = billingConfig.subscriptionNoticeRecurrence;
    setBillingConfig({ ...billingConfig, subscriptionNoticeRecurrence: value });
    setSavingNoticeRecurrence(true);
    try {
      const saved = await api.billing.updateCommunicationSettings(value);
      if (!saved?.subscriptionNoticeRecurrence) {
        setBillingConfig({ ...billingConfig, subscriptionNoticeRecurrence: previousValue });
        alert('Não foi possível salvar a recorrência dos avisos.');
      }
    } finally {
      setSavingNoticeRecurrence(false);
    }
  };

  const handleToggleAdminEmailPermission = async (admin: User) => {
    if (!canManageAdmins) return;
    if (admin.adminLevel === 0) return alert('Super Admin já tem acesso total aos e-mails.');
    await api.users.save({ ...admin, canSendEmails: !admin.canSendEmails });
    await loadAdmins();
  };

  useEffect(() => {
    handleRefresh();
    const interval = setInterval(() => {
      refreshInvites();
    }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const interval = window.setInterval(() => {
      handleAdminAutoRefresh();
    }, 90000);
    const handleFocus = () => handleAdminAutoRefresh();
    const handleVisibilityChange = () => {
      if (!document.hidden) handleAdminAutoRefresh();
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [handleAdminAutoRefresh]);

  useEffect(() => {
    if (activeTab === 'admins' && canViewTeam) {
      loadAdmins();
      loadTeamMessages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, canViewTeam]);

  const companiesList = useMemo(() => allCompanies || [], [allCompanies]);
  const selectedEmailCompanies = useMemo(
    () => companiesList.filter(company => selectedEmailCompanyIds.includes(company.id)),
    [companiesList, selectedEmailCompanyIds]
  );


  const canLeadPeople = (admin: User) => admin.adminLevel === 0 || Boolean(admin.canLeadTeam);
  const getCompanyOwnerAdminId = (company: Company) => company.ownerAdminId || company.referredById || '';
  const adminChildrenMap = useMemo(() => {
    const map = new Map<string, User[]>();
    const adminList = admins || [];
    const leaderIds = new Set(adminList.filter(canLeadPeople).map(admin => admin.id));
    const fallbackLeaderId = adminList.find(admin => admin.adminLevel === 0)?.id || 'ROOT';
    (admins || []).forEach(admin => {
      const isRootLeader = canLeadPeople(admin) && !admin.supervisorId;
      const supervisorId = isRootLeader
        ? 'ROOT'
        : admin.supervisorId && leaderIds.has(admin.supervisorId)
          ? admin.supervisorId
          : fallbackLeaderId;
      if (!map.has(supervisorId)) map.set(supervisorId, []);
      map.get(supervisorId)?.push(admin);
    });
    map.forEach(children => children.sort((a, b) => (a.adminLevel || 0) - (b.adminLevel || 0) || a.name.localeCompare(b.name)));
    return map;
  }, [admins]);

  const adminClientMap = useMemo(() => {
    const map = new Map<string, Company[]>();
    (admins || []).forEach(admin => map.set(admin.id, []));
    companiesList.forEach(company => {
      const ownerId = getCompanyOwnerAdminId(company);
      if (!ownerId) return;
      if (!map.has(ownerId)) map.set(ownerId, []);
      map.get(ownerId)?.push(company);
    });
    return map;
  }, [admins, companiesList]);

  const selectedTeamAdmin = useMemo(() => (admins || []).find(admin => admin.id === selectedTeamAdminId) || null, [admins, selectedTeamAdminId]);
  const selectedTeamAdminClients = selectedTeamAdmin ? (adminClientMap.get(selectedTeamAdmin.id) || []) : [];

  const visibleTeamRoots = useMemo(() => {
    const search = teamSearch.trim().toLowerCase();
    const roots = adminChildrenMap.get('ROOT') || [];
    if (!search) return roots;
    const matchesTree = (admin: User): boolean => {
      const clients = adminClientMap.get(admin.id) || [];
      const directMatch = admin.name.toLowerCase().includes(search)
        || admin.email.toLowerCase().includes(search)
        || clients.some(company => company.name.toLowerCase().includes(search) || company.email.toLowerCase().includes(search));
      const children = adminChildrenMap.get(admin.id) || [];
      return directMatch || children.some(matchesTree);
    };
    return roots.filter(matchesTree);
  }, [adminChildrenMap, adminClientMap, teamSearch]);

  const unassignedCompanies = useMemo(() => {
    const search = teamSearch.trim().toLowerCase();
    return companiesList.filter(company => !getCompanyOwnerAdminId(company)).filter(company => {
      if (!search) return true;
      return company.name.toLowerCase().includes(search) || company.email.toLowerCase().includes(search);
    });
  }, [companiesList, teamSearch]);

  const updateAdminTeamInfo = async (admin: User, data: Partial<User>) => {
    if (!canManageTeam) return;
    await api.users.save({ ...admin, ...data });
    await loadAdmins();
  };

  const updateCompanyTeamInfo = async (company: Company, data: Partial<Company>) => {
    if (!canManageTeam) return;
    const updated = await api.companies.updateSettings(company.id, data);
    if (!updated) return alert('No foi possvel atualizar a empresa agora.');
    await refreshData();
  };

  const assignCompanyOwner = async (company: Company, adminId: string) => {
    const selectedAdmin = (admins || []).find(admin => admin.id === adminId);
    await updateCompanyTeamInfo(company, {
      ownerAdminId: selectedAdmin?.id || '',
      ownerAdminName: selectedAdmin?.name || ''
    });
  };

  const editAdminContact = async (admin: User) => {
    if (!canManageTeam) return;
    const phone = window.prompt(`Telefone de ${admin.name}:`, admin.phone || '');
    if (phone === null) return;
    const position = window.prompt(`Cargo/função de ${admin.name}:`, admin.position || '');
    if (position === null) return;
    await updateAdminTeamInfo(admin, { phone: phone.trim(), position: position.trim() });
  };

  const editCompanyContact = async (company: Company) => {
    if (!canManageTeam) return;
    const phone = window.prompt(`Telefone de ${company.name}:`, company.contactPhone || '');
    if (phone === null) return;
    await updateCompanyTeamInfo(company, { contactPhone: phone.trim() });
  };

  const handleSendTeamMessage = async () => {
    if (!teamMessage.trim()) return alert('Escreva uma mensagem para a equipe.');
    setSavingTeam(true);
    try {
      const saved = await api.adminTeam.sendMessage({
        message: teamMessage.trim(),
        priority: teamPriority,
        targetAdminId: teamTargetAdminId || undefined,
        targetCompanyId: teamTargetCompanyId || undefined
      });
      if (!saved) return alert('No foi possvel enviar a mensagem interna.');
      setTeamMessage('');
      setTeamTargetAdminId('');
      setTeamTargetCompanyId('');
      setTeamPriority('NORMAL');
      setShowTeamMessageModal(false);
      await loadTeamMessages();
    } finally {
      setSavingTeam(false);
    }
  };

  const toggleTeamMessageStatus = async (message: AdminTeamMessage) => {
    const nextStatus = message.status === 'DONE' ? 'OPEN' : 'DONE';
    const updated = await api.adminTeam.updateMessage(message.id, nextStatus);
    if (updated) await loadTeamMessages();
  };

  const handleTeamCanvasMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (target.closest('input,select,textarea,button')) return;
    const canvas = teamCanvasRef.current;
    if (!canvas) return;
    teamPanRef.current = {
      active: true,
      x: event.clientX,
      y: event.clientY,
      startX: teamPanOffset.x,
      startY: teamPanOffset.y
    };
    event.preventDefault();
    document.body.style.userSelect = 'none';
    canvas.style.cursor = 'grabbing';
  };

  const handleTeamCanvasMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    const canvas = teamCanvasRef.current;
    if (!canvas || !teamPanRef.current.active) return;
    event.preventDefault();
    const nextX = teamPanRef.current.startX + (event.clientX - teamPanRef.current.x);
    setTeamPanOffset({
      x: Math.min(0, Math.max(nextX, -900)),
      y: 0
    });
  };

  const stopTeamCanvasPan = () => {
    teamPanRef.current.active = false;
    document.body.style.userSelect = '';
    if (teamCanvasRef.current) teamCanvasRef.current.style.cursor = 'grab';
  };

  const renderTeamNode = (admin: User, level = 0): React.ReactNode => {
    const children = adminChildrenMap.get(admin.id) || [];
    const clients = adminClientMap.get(admin.id) || [];
    const isSelected = selectedTeamAdminId === admin.id;

    return (
      <div className="relative flex flex-col items-center">
        <button
          type="button"
          onClick={() => setSelectedTeamAdminId(admin.id)}
          className={`group w-[260px] rounded-[1.5rem] border p-4 text-left shadow-lg transition ${
            isSelected
              ? 'border-emerald-300 bg-slate-950 text-white shadow-emerald-100'
              : 'border-slate-200 bg-white text-slate-950 shadow-slate-200/70 hover:-translate-y-0.5 hover:border-emerald-200'
          }`}
        >
          <div className="flex items-start gap-3">
            <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${isSelected ? 'bg-emerald-300 text-slate-950' : 'bg-slate-950 text-white'}`}>
              <UserCog className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                {(admin.adminLevel === 0 || admin.canLeadTeam) && <span className="rounded-full bg-emerald-100 px-2 py-1 text-[9px] font-black uppercase tracking-wider text-emerald-700">Chefe</span>}
                <span className={`rounded-full px-2 py-1 text-[9px] font-black uppercase tracking-wider ${isSelected ? 'bg-white/10 text-emerald-100' : 'bg-slate-100 text-slate-500'}`}>
                  {getAdminLabel(admin.adminLevel)}
                </span>
              </div>
              <h4 className="mt-2 truncate text-base font-black">{admin.name}</h4>
              <p className={`truncate text-[11px] font-semibold ${isSelected ? 'text-slate-300' : 'text-slate-500'}`}>{admin.email}</p>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2 text-center">
            <div className={`rounded-2xl p-2 ${isSelected ? 'bg-white/10' : 'bg-slate-50'}`}>
              <p className="text-lg font-black">{children.length}</p>
              <p className={`text-[9px] font-black uppercase tracking-wider ${isSelected ? 'text-slate-300' : 'text-slate-400'}`}>Equipe</p>
            </div>
            <div className={`rounded-2xl p-2 ${isSelected ? 'bg-white/10' : 'bg-slate-50'}`}>
              <p className="text-lg font-black">{clients.length}</p>
              <p className={`text-[9px] font-black uppercase tracking-wider ${isSelected ? 'text-slate-300' : 'text-slate-400'}`}>Clientes</p>
            </div>
          </div>
          {(admin.phone || admin.position) && (
            <p className={`mt-3 truncate text-[11px] font-bold ${isSelected ? 'text-slate-300' : 'text-slate-500'}`}>
              {[admin.position, admin.phone].filter(Boolean).join(' âââ€š¬¢ ')}
            </p>
          )}
        </button>

        {canManageTeam && (
          <div className="mt-2 grid w-[260px] grid-cols-2 gap-2">
            <button onClick={() => editAdminContact(admin)} className="flex-1 rounded-xl bg-slate-100 px-3 py-2 text-[10px] font-black text-slate-600 transition hover:bg-slate-200">Editar</button>
            {canManageAdmins && admin.adminLevel !== 0 && (
              <button
                onClick={() => updateAdminTeamInfo(admin, {
                  canLeadTeam: !admin.canLeadTeam,
                  supervisorId: admin.canLeadTeam ? '' : admin.supervisorId,
                  supervisorName: admin.canLeadTeam ? '' : admin.supervisorName
                })}
                className={`rounded-xl px-3 py-2 text-[10px] font-black transition ${
                  admin.canLeadTeam
                    ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                    : 'bg-slate-950 text-white hover:bg-slate-800'
                }`}
              >
                {admin.canLeadTeam ? 'Chefe ativo' : 'Tornar chefe'}
              </button>
            )}
            <select
              disabled={!canManageAdmins || admin.adminLevel === 0}
              value={admin.supervisorId || ''}
              onChange={event => {
                const supervisor = admins.find(item => item.id === event.target.value);
                updateAdminTeamInfo(admin, { supervisorId: supervisor?.id || '', supervisorName: supervisor?.name || '' });
              }}
              className="col-span-2 min-w-0 rounded-xl border border-slate-200 bg-white px-2 py-2 text-[10px] font-bold outline-none disabled:opacity-60"
            >
              <option value="">{admin.canLeadTeam ? 'Chefe principal' : 'Direto com Super Admin'}</option>
              {admins.filter(item => item.id !== admin.id && canLeadPeople(item)).map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </div>
        )}

        {children.length > 0 && (
          <>
            <div className="h-8 w-px bg-slate-300" />
            <div className="relative flex items-start gap-5 pt-8">
              <div className="absolute left-0 right-0 top-0 h-px bg-slate-300" />
              {children.map(child => (
                <div key={child.id} className="relative">
                  <div className="absolute left-1/2 top-[-2rem] h-8 w-px -translate-x-1/2 bg-slate-300" />
                  {renderTeamNode(child, level + 1)}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  const metrics = useMemo(() => {
    const active = companiesList.filter(company =>
      company.subscriptionStatus === SubscriptionStatus.ACTIVE && (daysUntil(company.nextBillingDate) ?? 0) >= 0
    );
    const trial = companiesList.filter(company =>
      company.subscriptionStatus === SubscriptionStatus.TRIAL && (daysUntil(company.nextBillingDate) ?? 0) >= 0
    );
    const blocked = companiesList.filter(company =>
      [SubscriptionStatus.PENDING, SubscriptionStatus.CANCELED].includes(company.subscriptionStatus)
      || (daysUntil(company.nextBillingDate) ?? 0) < 0
    );
    const expiringSoon = companiesList.filter(company => {
      const days = daysUntil(company.nextBillingDate);
      const canExpire = [SubscriptionStatus.ACTIVE, SubscriptionStatus.TRIAL].includes(company.subscriptionStatus);
      return canExpire && days !== null && days >= 0 && days <= 7;
    });
    const estimatedMrr = active.reduce((sum, company) => sum + getCompanyMonthlyPrice(company), 0);
    const enabledDelivery = companiesList.filter(company => company.useDelivery).length;
    const enabledStock = companiesList.filter(company => company.useStockManagement).length;

    return {
      active,
      trial,
      blocked,
      expiringSoon,
      estimatedMrr,
      enabledDelivery,
      enabledStock,
    };
  }, [companiesList]);

  const planCounts = useMemo(() => {
    return companiesList.reduce<Record<string, number>>((acc, company) => {
      acc[company.plan] = (acc[company.plan] || 0) + 1;
      return acc;
    }, {});
  }, [companiesList]);

  const handleGenerateCode = async () => {
    setGeneratingCode(true);
    try {
      const result = await generateInviteCode();
      if (!result.success) alert(result.message);
      else await refreshInvites();
    } finally {
      setGeneratingCode(false);
    }
  };

  const handleDeleteInvite = async (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir este código de convite')) {
      await deleteInviteCode(id);
      await refreshInvites();
    }
  };

  const handleCreateAdmin = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canManageAdmins) return;

    setLoadingAction(true);
    try {
      const result = await createAdmin(newAdminName, newAdminEmail, newAdminPass, newAdminLevel, newAdminCanSendEmails, newAdminCanLeadTeam);
      if (result?.success) {
        alert('Administrador criado com sucesso!');
        setShowAdminModal(false);
        setNewAdminName('');
        setNewAdminEmail('');
        setNewAdminPass('');
        setNewAdminCanSendEmails(false);
        setNewAdminCanLeadTeam(false);
        await loadAdmins();
      } else {
        alert(result.message);
      }
    } finally {
      setLoadingAction(false);
    }
  };

  const handleDeleteAdmin = async (admin: User) => {
    if (!canManageAdmins) return;
    if (user?.id === admin.id) return alert('Você não pode apagar sua própria conta.');
    if (window.confirm(`Tem certeza que deseja remover o acesso administrativo de ${admin.name}?`)) {
      await deleteAdmin(admin.id);
      await loadAdmins();
    }
  };

  const handleToggleStatus = async (company: Company) => {
    if (!canManageBilling) return;

    setLoadingAction(true);
    try {
      if (company.subscriptionStatus === SubscriptionStatus.ACTIVE || company.subscriptionStatus === SubscriptionStatus.TRIAL) {
        if (window.confirm(`Deseja pausar o acesso da empresa "${company.name}"?`)) {
          const saved = await toggleCompanyStatus(company.id, SubscriptionStatus.PENDING);
          if (!saved) {
            alert('Não foi possível pausar a empresa agora. Atualize e tente novamente.');
            await refreshData();
          }
        }
        return;
      }

      const defaultDate = getDateAfterDaysForInput(30);
      const newDate = window.prompt(
        `Informe o novo vencimento da empresa "${company.name}".\n\nSe quiser liberar por 30 dias, só confirme a data abaixo:`,
        defaultDate
      );

      if (!newDate) return;
      const parsedDate = new Date(`${newDate}T00:00:00`);
      if (Number.isNaN(parsedDate.getTime())) {
        alert('Data inválida. Use o formato AAAA-MM-DD.');
        return;
      }

      const saved = await updateCompanyBillingDate(company.id, newDate, true);
      if (!saved) {
        alert('Não foi possível ativar a empresa agora. Atualize e tente novamente.');
        await refreshData();
      }
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      alert('Não foi possível alterar o status da empresa agora.');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleCreateBillingLink = async (company: Company) => {
    if (!canManageBilling) return;
    setLoadingAction(true);
    try {
      const response = await api.billing.createMercadoPagoSubscription(company.id, company.plan);
      if (response?.error) {
        alert(response.error);
        return;
      }
      if (response?.initPoint) {
        window.open(response.initPoint, '_blank', 'noopener,noreferrer');
        return;
      }
      alert('Não foi possível gerar a cobrança agora.');
    } catch (error) {
      console.error('Erro ao gerar cobrança:', error);
      alert('Não foi possível gerar a cobrança agora.');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleChangeCompanyPlan = async (company: Company, plan: BillingPlanKey) => {
    if (!isSuperAdmin || company.plan === plan) return;

    const planConfig = billingConfig?.plans.find(item => item.plan === plan);
    const featureUpdates = planConfig
      ? Object.fromEntries(
          Object.entries(planConfig.features).map(([key, enabled]) => [key, Boolean(enabled)])
        )
      : {};

    setLoadingAction(true);
    try {
      const saved = await api.companies.save({
        ...company,
        plan,
        ...featureUpdates
      });
      if (!saved) {
        alert('Não foi possível alterar o plano agora.');
        return;
      }
      await refreshData();
    } catch (error) {
      console.error('Erro ao alterar plano:', error);
      alert('Não foi possível alterar o plano agora.');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleChangeCompanyPrice = async (company: Company) => {
    if (!isSuperAdmin) return;

    const currentPrice = getCompanyMonthlyPrice(company);
    const typedPrice = window.prompt(
      `Preço mensal individual para "${company.name}".\n\nDeixe vazio para voltar ao preço padrão do plano ${company.plan}.`,
      String(currentPrice).replace('.', ',')
    );

    if (typedPrice === null) return;

    const cleanedPrice = typedPrice.trim();
    const monthlyPriceOverride = cleanedPrice ? parseCurrencyInput(cleanedPrice) : null;

    if (monthlyPriceOverride !== null && (!Number.isFinite(monthlyPriceOverride) || monthlyPriceOverride < 1)) {
      alert('Preço inválido. Use um valor maior ou igual a R$ 1,00.');
      return;
    }

    setLoadingAction(true);
    try {
      const saved = await api.companies.save({
        ...company,
        monthlyPriceOverride
      });
      if (!saved) {
        alert('Não foi possível alterar o preço individual agora.');
        return;
      }
      await refreshData();
    } catch (error) {
      console.error('Erro ao alterar preço individual:', error);
      alert('Não foi possível alterar o preço individual agora.');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleChangeCompanyInvoiceLimit = async (company: Company) => {
    if (!isSuperAdmin) return;

    const typedLimit = window.prompt(
      `Limite mensal individual de leituras por foto para "${company.name}".\n\nDeixe vazio para usar o limite padrão do plano. Use 0 para bloquear esta empresa.`,
      company.invoicePhotoOcrMonthlyLimit === null || company.invoicePhotoOcrMonthlyLimit === undefined
        ? ''
        : String(company.invoicePhotoOcrMonthlyLimit)
    );
    if (typedLimit === null) return;

    const cleanedLimit = typedLimit.trim();
    const invoicePhotoOcrMonthlyLimit = cleanedLimit === '' ? null : Number(cleanedLimit);
    if (invoicePhotoOcrMonthlyLimit !== null && (!Number.isInteger(invoicePhotoOcrMonthlyLimit) || invoicePhotoOcrMonthlyLimit < 0 || invoicePhotoOcrMonthlyLimit > 100000)) {
      alert('Informe um número inteiro entre 0 e 100.000.');
      return;
    }

    setLoadingAction(true);
    try {
      const saved = await api.companies.save({ ...company, invoicePhotoOcrMonthlyLimit });
      if (!saved) {
        alert('Não foi possível salvar o limite individual agora.');
        return;
      }
      await refreshData();
    } catch (error) {
      console.error('Erro ao alterar limite individual de leitura:', error);
      alert('Não foi possível salvar o limite individual agora.');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleSaveTextractLimit = async () => {
    if (!isSuperAdmin || !billingConfig || savingTextractLimit) return;
    const globalMonthlyLimit = Number(billingConfig.textract?.globalMonthlyLimit || 0);
    if (!Number.isInteger(globalMonthlyLimit) || globalMonthlyLimit < 1 || globalMonthlyLimit > 100000) {
      alert('Informe um limite global inteiro entre 1 e 100.000.');
      return;
    }

    setSavingTextractLimit(true);
    try {
      const saved = await api.billing.updateTextractSettings(globalMonthlyLimit);
      if (!saved?.globalMonthlyLimit) {
        alert('Não foi possível salvar o teto global agora.');
        return;
      }
      setBillingConfig(current => current ? {
        ...current,
        textract: { configured: current.textract?.configured || false, globalMonthlyLimit: saved.globalMonthlyLimit! }
      } : current);
    } catch (error) {
      console.error('Erro ao alterar teto global do Textract:', error);
      alert('Não foi possível salvar o teto global agora.');
    } finally {
      setSavingTextractLimit(false);
    }
  };

  const handleChangeCompanyBillingEmail = async (company: Company) => {
    if (!isSuperAdmin) return;

    const typedEmail = window.prompt(
      `E-mail de cobrança Mercado Pago para "${company.name}".\n\nDeixe vazio para usar o e-mail principal da empresa.`,
      company.billingEmail || company.email || ''
    );

    if (typedEmail === null) return;

    const billingEmail = typedEmail.trim().toLowerCase();
    if (billingEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(billingEmail)) {
      alert('E-mail de cobrança inválido.');
      return;
    }

    setLoadingAction(true);
    try {
      const saved = await api.companies.save({
        ...company,
        billingEmail: billingEmail || null
      });
      if (!saved) {
        alert('Não foi possível alterar o e-mail de cobrança agora.');
        return;
      }
      await refreshData();
    } catch (error) {
      console.error('Erro ao alterar e-mail de cobrança:', error);
      alert('Não foi possível alterar o e-mail de cobrança agora.');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleUpdateBillingDate = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canManageBilling) return;
    if (!dateEditCompanyId || !newBillingDate) {
      alert('Por favor, selecione uma data válida.');
      return;
    }

    const shouldActivate = window.confirm('Deseja marcar a conta como ativa automaticamente com esta nova data');
    setLoadingAction(true);
    try {
      await updateCompanyBillingDate(dateEditCompanyId, newBillingDate, shouldActivate);
      setDateEditCompanyId(null);
      setNewBillingDate('');
    } catch (error) {
      console.error('Erro ao atualizar vencimento:', error);
    } finally {
      setLoadingAction(false);
    }
  };

  const handleDeleteCompany = async (id: string, name: string) => {
    if (!canDeleteCompanies) return;
    if (window.confirm(`ATENÃƒâ€¡ÃƒÆ’O: deseja realmente excluir a empresa "${name}"?

Esta ação é irreversível.`)) {
      await deleteCompany(id);
    }
  };

  const handleUpdatePassword = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canManageBilling || !editingCompanyId || !newPassword) return;

    setLoadingAction(true);
    try {
      const success = await adminUpdatePassword(editingCompanyId, newPassword);
      if (success) {
        alert('Senha resetada com sucesso!');
        setEditingCompanyId(null);
        setNewPassword('');
      } else {
        alert('Erro ao resetar senha.');
      }
    } finally {
      setLoadingAction(false);
    }
  };

  const copyInvite = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      alert('Código copiado!');
    } catch {
      alert(`Código: ${code}`);
    }
  };

  const insertCompanyVariable = (
    value: string,
    setter: React.Dispatch<React.SetStateAction<string>>
  ) => {
    const separator = value && !value.endsWith(' ') ? ' ' : '';
    setter(`${value}${separator}@empresa`);
  };

  const renderCompanyVariableField = (
    label: string,
    value: string,
    setter: React.Dispatch<React.SetStateAction<string>>,
    placeholder: string,
    rows = 5
  ) => {
    const isUsingCompanyVariable = value.toLowerCase().includes('@empresa');
    const isTypingVariable = /@\w*$/i.test(value.trim());

    return (
      <div className={`rounded-2xl border bg-slate-50 p-3 transition ${
        isUsingCompanyVariable
          ? 'border-emerald-300 ring-4 ring-emerald-50'
          : isTypingVariable
            ? 'border-amber-300 ring-4 ring-amber-50'
            : 'border-slate-200'
      }`}>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">{label}</span>
          <button
            type="button"
            onClick={() => insertCompanyVariable(value, setter)}
            className={`rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-wider transition ${
              isUsingCompanyVariable
                ? 'bg-emerald-600 text-white'
                : 'bg-white text-emerald-700 ring-1 ring-emerald-100 hover:bg-emerald-50'
            }`}
          >
            {isUsingCompanyVariable ? '@empresa ativo' : 'Inserir @empresa'}
          </button>
        </div>
        <textarea
          value={value}
          onChange={event => setter(event.target.value)}
          placeholder={placeholder}
          rows={rows}
          className="w-full resize-none bg-transparent text-sm font-bold text-slate-800 outline-none placeholder:text-slate-400"
        />
        {(isTypingVariable || isUsingCompanyVariable) && (
          <div className="mt-2 rounded-xl border border-emerald-100 bg-white px-3 py-2 text-xs font-bold text-slate-600">
            <span className="rounded-full bg-emerald-100 px-2 py-1 text-[10px] font-black uppercase text-emerald-700">@empresa</span>
            <span className="ml-2">será trocado pelo nome real de cada cliente no envio.</span>
          </div>
        )}
      </div>
    );
  };

  if (!user || user.role !== UserRole.SAAS_ADMIN) {
    return (
      <div className="flex h-screen flex-col items-center justify-center bg-slate-50 p-4 text-center">
        <div className="mb-5 rounded-[2rem] bg-red-50 p-5 text-red-400 ring-1 ring-red-100">
          <Lock className="h-14 w-14" />
        </div>
        <h2 className="text-2xl font-black text-slate-900">Acesso negado</h2>
        <p className="mt-2 max-w-md text-sm font-medium text-slate-500">
          Apenas administradores da plataforma podem acessar este painel.
        </p>
        <Button onClick={() => { window.location.href = '#/'; }} className="mt-5">
          Voltar ao início
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex-1 overflow-y-auto bg-[#eef3f8] text-slate-900 no-scrollbar">
      <div className="relative overflow-hidden bg-slate-950">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(35,214,161,0.28),transparent_34rem),radial-gradient(circle_at_top_right,rgba(59,130,246,0.22),transparent_30rem)]" />
        <div className={`relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ${activeTab === 'dashboard' ? 'py-8' : 'py-5'}`}>
          <header className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1.5 text-xs font-black uppercase tracking-[0.2em] text-emerald-200">
                <ShieldCheck className="h-4 w-4" />
                FlowTab Command Center
              </div>
              <h1 className={`${activeTab === 'dashboard' ? 'mt-5 text-4xl sm:text-5xl' : 'mt-4 text-3xl sm:text-4xl'} font-black tracking-tight text-white`}>
                {activeTab === 'dashboard'
                  ? 'Painel empresarial'
                  : activeTab === 'admins'
                    ? 'Organograma empresarial'
                    : activeTab === 'emails'
                      ? 'Central de e-mails'
                      : activeTab === 'plans'
                        ? 'Planos FlowTab'
                      : 'Empresas cadastradas'}
              </h1>
              <p className="mt-3 max-w-2xl text-sm font-medium leading-6 text-slate-300 sm:text-base">
                {activeTab === 'dashboard'
                  ? 'Visão executiva dos clientes, convites, planos, vencimentos e acessos administrativos da plataforma.'
                  : activeTab === 'admins'
                    ? 'Organograma interno da empresa, responsáveis, contatos e comunicação administrativa.'
                    : activeTab === 'emails'
                      ? 'Comunicados, previas e campanhas com seguranca por permissao.'
                      : activeTab === 'plans'
                        ? 'Precos, recursos e limites comerciais de cada pacote da plataforma.'
                      : 'Status comercial, vencimentos, recursos ativos e acoes por nivel.'}
              </p>
            </div>

            <div className={`${activeTab === 'dashboard' ? 'grid' : 'hidden lg:grid'} gap-3 sm:grid-cols-2 lg:min-w-[360px]`}>
              <div className="rounded-[1.5rem] border border-white/10 bg-white/10 p-4 text-white backdrop-blur">
                <p className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-300">Seu acesso</p>
                <div className="mt-3 flex items-center gap-3">
                  <div className="rounded-2xl bg-emerald-400/15 p-3 text-emerald-200">
                    <Crown className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-black">{getAdminLabel(user.adminLevel)}</p>
                    <p className="text-xs font-medium text-slate-300">{getAdminDescription(user.adminLevel)}</p>
                  </div>
                </div>
              </div>

              <div className="rounded-[1.5rem] border border-white/10 bg-white/10 p-4 text-white backdrop-blur">
                <p className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-300">Ações seguras</p>
                <p className="mt-3 text-sm font-bold text-slate-100">
                  {canManageBilling ? 'Cobrança liberada' : 'Somente acompanhamento'}
                </p>
                <p className="mt-1 text-xs font-medium text-slate-300">
                  Permissões respeitam o nível do administrador logado.
                </p>
              </div>
            </div>
          </header>

          {activeTab === 'dashboard' && <section className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <PanelCard
              title="Receita estimada"
              value={formatCurrency(metrics.estimatedMrr)}
              description="MRR calculado por plano ativo"
              icon={WalletCards}
              tone="emerald"
            />
            <PanelCard
              title="Empresas ativas"
              value={metrics.active.length}
              description={`${companiesList.length} empresas cadastradas`}
              icon={Building2}
              tone="dark"
            />
            <PanelCard
              title="Trials"
              value={metrics.trial.length}
              description="Contas em período de teste"
              icon={Sparkles}
              tone="sky"
            />
            <PanelCard
              title="Atenção"
              value={metrics.blocked.length}
              description={`${metrics.expiringSoon.length} vencem em até 7 dias`}
              icon={Activity}
              tone={metrics.blocked.length > 0 ? 'rose' : 'amber'}
            />
          </section>}
        </div>
      </div>

      <main className="mx-auto -mt-5 max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
        <div className="grid gap-5">
          <section className="space-y-5">
            <div className="rounded-[2rem] border border-white/70 bg-white/90 p-3 shadow-xl shadow-slate-200/70 backdrop-blur">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex gap-2 overflow-x-auto no-scrollbar">
                  <button
                    onClick={() => setActiveTab('dashboard')}
                    className={`flex min-w-fit items-center gap-2 rounded-2xl px-4 py-3 text-sm font-black transition ${
                      activeTab === 'dashboard' ? 'bg-slate-950 text-white shadow-lg' : 'text-slate-500 hover:bg-slate-100'
                    }`}
                  >
                    <LayoutDashboard className="h-4 w-4" />
                    Painel
                  </button>

                  <button
                    onClick={() => setActiveTab('companies')}
                    className={`flex min-w-fit items-center gap-2 rounded-2xl px-4 py-3 text-sm font-black transition ${
                      activeTab === 'companies' ? 'bg-slate-950 text-white shadow-lg' : 'text-slate-500 hover:bg-slate-100'
                    }`}
                  >
                    <Building2 className="h-4 w-4" />
                    Empresas
                  </button>

                  {canViewTeam && (
                    <button
                      onClick={() => setActiveTab('admins')}
                      className={`flex min-w-fit items-center gap-2 rounded-2xl px-4 py-3 text-sm font-black transition ${
                        activeTab === 'admins' ? 'bg-slate-950 text-white shadow-lg' : 'text-slate-500 hover:bg-slate-100'
                      }`}
                    >
                      <Users className="h-4 w-4" />
                      Organograma
                    </button>
                  )}
                  {canUseEmailCenter && (
                    <button
                      onClick={() => setActiveTab('emails')}
                      className={`flex min-w-fit items-center gap-2 rounded-2xl px-4 py-3 text-sm font-black transition ${
                        activeTab === 'emails' ? 'bg-slate-950 text-white shadow-lg' : 'text-slate-500 hover:bg-slate-100'
                      }`}
                    >
                      <Mail className="h-4 w-4" />
                      E-mails
                    </button>
                  )}
                  {canManageBilling && (
                    <button
                      onClick={() => setActiveTab('plans')}
                      className={`flex min-w-fit items-center gap-2 rounded-2xl px-4 py-3 text-sm font-black transition ${
                        activeTab === 'plans' ? 'bg-slate-950 text-white shadow-lg' : 'text-slate-500 hover:bg-slate-100'
                      }`}
                    >
                      <WalletCards className="h-4 w-4" />
                      Planos
                    </button>
                  )}
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button variant="secondary" size="sm" onClick={handleRefresh}>
                    <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                    Atualizar
                  </Button>
                  {canManageAdmins && (
                    <Button size="sm" onClick={() => setShowAdminModal(true)}>
                      <Plus className="h-4 w-4" />
                      Novo admin
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {activeTab === 'dashboard' ? (
              <div className="grid gap-4 xl:grid-cols-4">
                <section className="rounded-[1.75rem] border border-white/70 bg-white p-5 shadow-xl shadow-slate-200/70 xl:col-span-2">
                  <p className="text-[11px] font-black uppercase tracking-[0.22em] text-emerald-600">Resumo executivo</p>
                  <h2 className="mt-1 text-2xl font-black text-slate-950">Operação FlowTab</h2>
                  <div className="mt-4 grid gap-3 sm:grid-cols-4">
                    <div className="rounded-2xl bg-slate-50 p-4"><p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Estoque</p><p className="mt-2 text-3xl font-black text-slate-950">{metrics.enabledStock}</p></div>
                    <div className="rounded-2xl bg-slate-50 p-4"><p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Delivery</p><p className="mt-2 text-3xl font-black text-slate-950">{metrics.enabledDelivery}</p></div>
                    <div className="rounded-2xl bg-amber-50 p-4"><p className="text-[10px] font-black uppercase tracking-wider text-amber-600">Vencendo</p><p className="mt-2 text-3xl font-black text-amber-700">{metrics.expiringSoon.length}</p></div>
                    <div className="rounded-2xl bg-rose-50 p-4"><p className="text-[10px] font-black uppercase tracking-wider text-rose-600">Pausadas</p><p className="mt-2 text-3xl font-black text-rose-700">{metrics.blocked.length}</p></div>
                  </div>
                  <div className="mt-4 grid gap-2 sm:grid-cols-3">
                    <button onClick={() => setActiveTab('companies')} className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3 text-left text-sm font-black text-slate-700 transition hover:bg-slate-100">Empresas <ArrowUpRight className="h-4 w-4" /></button>
                    {canViewTeam && <button onClick={() => setActiveTab('admins')} className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3 text-left text-sm font-black text-slate-700 transition hover:bg-slate-100">Organograma <ArrowUpRight className="h-4 w-4" /></button>}
                    {canUseEmailCenter && <button onClick={() => setActiveTab('emails')} className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3 text-left text-sm font-black text-slate-700 transition hover:bg-slate-100">E-mails <ArrowUpRight className="h-4 w-4" /></button>}
                    {canManageBilling && <button onClick={() => setActiveTab('plans')} className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3 text-left text-sm font-black text-slate-700 transition hover:bg-slate-100">Planos <ArrowUpRight className="h-4 w-4" /></button>}
                  </div>
                </section>

                <section className="rounded-[1.75rem] border border-white/70 bg-white p-5 shadow-xl shadow-slate-200/70">
                  <div className="flex items-center justify-between gap-3"><div><h2 className="text-lg font-black text-slate-950">Convites</h2><p className="text-xs font-semibold text-slate-500">Novos cadastros.</p></div><Ticket className="h-5 w-5 text-emerald-600" /></div>
                  <Button onClick={handleGenerateCode} disabled={(activeInvites || []).length >= 5 || generatingCode} className="mt-4 w-full rounded-2xl">{generatingCode ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}Gerar código</Button>
                  <div className="mt-3 max-h-28 space-y-2 overflow-y-auto pr-1">
                    {(activeInvites || []).length === 0 ? <p className="rounded-2xl bg-slate-50 p-3 text-center text-xs font-bold text-slate-400">Nenhum código ativo.</p> : activeInvites.map(invite => <div key={invite.id} className="flex items-center justify-between rounded-2xl bg-emerald-50 px-3 py-2 text-xs font-black text-emerald-700"><span>{invite.code}</span><button onClick={() => copyInvite(invite.code)}><Copy className="h-4 w-4" /></button></div>)}
                  </div>
                </section>

                <section className="rounded-[1.75rem] border border-white/70 bg-slate-950 p-5 text-white shadow-xl shadow-slate-200/70">
                  <div className="flex items-center justify-between gap-3"><div><h2 className="text-lg font-black">Base e planos</h2><p className="text-xs font-semibold text-slate-300">Tudo em uma visão.</p></div><Activity className="h-5 w-5 text-emerald-300" /></div>
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center justify-between rounded-2xl bg-white/10 px-3 py-2 text-sm font-black"><span>BASIC</span><span>{planCounts.BASIC || 0}</span></div>
                    <div className="flex items-center justify-between rounded-2xl bg-white/10 px-3 py-2 text-sm font-black"><span>PRO</span><span>{planCounts.PRO || 0}</span></div>
                    <div className="flex items-center justify-between rounded-2xl bg-white/10 px-3 py-2 text-sm font-black"><span>ENTERPRISE</span><span>{planCounts.ENTERPRISE || 0}</span></div>
                  </div>
                </section>
              </div>
            ) : activeTab === 'plans' ? (
              <div className="space-y-5">
                <section className="overflow-hidden rounded-[2rem] border border-white/70 bg-slate-950 text-white shadow-xl shadow-slate-200/70">
                  <div className="grid gap-6 p-6 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
                    <div>
                      <p className="text-[11px] font-black uppercase tracking-[0.24em] text-emerald-300">Monetização FlowTab</p>
                      <h2 className="mt-2 text-3xl font-black tracking-tight lg:text-4xl">Central de planos e cobrança</h2>
                      <p className="mt-3 max-w-2xl text-sm font-semibold leading-6 text-slate-300">
                        Controle preços, recursos e integração de pagamento em um painel único. A liberação da empresa continua dependendo da confirmação segura do backend.
                      </p>
                      <div className="mt-5 flex flex-wrap gap-2 text-[11px] font-black uppercase tracking-wider">
                        <span className="rounded-full bg-white/10 px-3 py-2 text-slate-200 ring-1 ring-white/10">Somente super admin</span>
                        <span className="rounded-full bg-emerald-400/10 px-3 py-2 text-emerald-200 ring-1 ring-emerald-300/20">Webhook automático</span>
                        <span className="rounded-full bg-sky-400/10 px-3 py-2 text-sky-200 ring-1 ring-sky-300/20">Mercado Pago</span>
                      </div>
                    </div>
                    <div className="grid gap-3">
                      <div className={`rounded-[1.6rem] p-4 ring-1 ${billingConfig?.checkoutConfigured ? 'bg-emerald-400/10 text-emerald-100 ring-emerald-300/20' : 'bg-amber-400/10 text-amber-100 ring-amber-300/20'}`}>
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="text-[10px] font-black uppercase tracking-widest opacity-75">Mercado Pago</p>
                            <p className="mt-1 text-xl font-black">{billingConfig?.checkoutConfigured ? 'Conectado' : 'Pendente'}</p>
                          </div>
                          <WalletCards className="h-8 w-8 opacity-80" />
                        </div>
                        <p className="mt-3 text-xs font-bold opacity-80">
                          {billingConfig?.checkoutConfigured
                            ? 'Cobranças podem ser geradas com token configurado.'
                            : 'Configure o token para liberar assinatura online.'}
                        </p>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="rounded-[1.4rem] bg-white/10 p-4 ring-1 ring-white/10">
                          <p className="text-[10px] font-black uppercase tracking-widest text-slate-300">Webhook</p>
                          <p className="mt-2 break-all text-xs font-bold text-slate-200">/webhooks/mercadopago</p>
                        </div>
                        <div className="rounded-[1.4rem] bg-white/10 p-4 ring-1 ring-white/10">
                          <p className="text-[10px] font-black uppercase tracking-widest text-slate-300">Planos ativos</p>
                          <p className="mt-2 text-2xl font-black text-white">{billingConfig?.plans?.length || 0}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                <section className="rounded-[2rem] border border-cyan-100 bg-gradient-to-br from-cyan-50 via-white to-emerald-50 p-5 shadow-sm">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-[0.22em] text-cyan-700">Proteção de orçamento</p>
                      <h3 className="mt-1 text-xl font-black text-slate-950">Limite global do leitor de notas</h3>
                      <p className="mt-1 max-w-2xl text-xs font-semibold leading-5 text-slate-500">Ao atingir este teto, o Textract é bloqueado para todas as empresas até o próximo mês. Cada empresa ainda pode receber um limite individual.</p>
                    </div>
                    <div className="flex w-full max-w-md items-end gap-2">
                      <label className="min-w-0 flex-1">
                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Leituras por mês</span>
                        <input
                          type="number"
                          min={1}
                          max={100000}
                          value={billingConfig?.textract?.globalMonthlyLimit || ''}
                          disabled={!isSuperAdmin}
                          onChange={event => setBillingConfig(current => current ? {
                            ...current,
                            textract: {
                              configured: current.textract?.configured || false,
                              globalMonthlyLimit: Number(event.target.value || 0)
                            }
                          } : current)}
                          className="mt-2 w-full rounded-2xl border border-cyan-200 bg-white px-4 py-3 text-lg font-black text-slate-950 outline-none focus:border-cyan-500 disabled:cursor-not-allowed disabled:opacity-60"
                        />
                      </label>
                      <Button onClick={handleSaveTextractLimit} disabled={!isSuperAdmin || savingTextractLimit} className="rounded-2xl bg-cyan-700 px-4 hover:bg-cyan-800">
                        {savingTextractLimit ? <RefreshCw className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                        Salvar teto
                      </Button>
                    </div>
                  </div>
                  <p className={`mt-3 text-xs font-bold ${billingConfig?.textract?.configured ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {billingConfig?.textract?.configured ? 'Textract conectado.' : 'Textract ainda não foi encontrado nas variáveis do servidor.'}
                  </p>
                </section>

                <section className="grid gap-4 lg:grid-cols-3">
                  {(billingConfig?.plans || []).map((planConfig, index) => {
                    const featured = planConfig.plan === 'PRO';
                    const featureKeys = Object.keys(billingConfig?.featureCatalog || {});
                    const activeFeatureCount = featureKeys.filter(featureKey => planConfig.features[featureKey]).length;
                    return (
                      <article key={planConfig.plan} className={`flex min-h-full flex-col rounded-[2rem] border bg-white p-5 shadow-xl shadow-slate-200/70 ${featured ? 'border-emerald-200 ring-2 ring-emerald-100' : 'border-white/70'}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-400">Plano</p>
                            <h3 className="mt-1 text-2xl font-black text-slate-950">{planConfig.plan}</h3>
                            <p className="mt-1 text-xs font-bold text-slate-500">
                              {index === 0 ? 'Entrada enxuta para começar bem.' : index === 1 ? 'Melhor custo-benefício para operação real.' : 'Pacote completo para operação exigente.'}
                            </p>
                          </div>
                          {featured && <span className="rounded-full bg-emerald-100 px-3 py-1 text-[10px] font-black uppercase text-emerald-700">Mais indicado</span>}
                        </div>

                        <div className="mt-5 grid grid-cols-2 gap-3">
                          <div className="rounded-2xl bg-slate-50 p-3">
                            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Recursos</p>
                            <p className="mt-1 text-2xl font-black text-slate-950">{activeFeatureCount}</p>
                          </div>
                          <div className="rounded-2xl bg-emerald-50 p-3">
                            <p className="text-[10px] font-black uppercase tracking-widest text-emerald-600">Status</p>
                            <p className="mt-1 text-sm font-black text-emerald-700">{featured ? 'Principal' : 'Ativo'}</p>
                          </div>
                        </div>

                        <label className="mt-4 block">
                          <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Preço mensal</span>
                          <div className="mt-2 flex items-center rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 focus-within:border-emerald-400 focus-within:bg-white">
                            <span className="mr-2 text-sm font-black text-slate-400">R$</span>
                            <input
                              type="number"
                              min={1}
                              step="0.01"
                              value={planConfig.price}
                              disabled={!isSuperAdmin}
                              onChange={event => updatePlanDraft(planConfig.plan, current => ({ ...current, price: Number(event.target.value) }))}
                              className="w-full bg-transparent text-2xl font-black text-slate-950 outline-none disabled:opacity-70"
                            />
                          </div>
                        </label>

                        <div className="mt-5 flex-1 space-y-2">
                          {featureKeys.map(featureKey => (
                            <label key={featureKey} className="flex cursor-pointer items-center justify-between gap-3 rounded-2xl bg-slate-50 px-3 py-2.5 transition hover:bg-slate-100">
                              <span className="text-sm font-black text-slate-700">{billingConfig?.featureCatalog[featureKey]}</span>
                              <input
                                type="checkbox"
                                checked={Boolean(planConfig.features[featureKey])}
                                disabled={!isSuperAdmin}
                                onChange={event => updatePlanDraft(planConfig.plan, current => ({
                                  ...current,
                                  features: { ...current.features, [featureKey]: event.target.checked }
                                }))}
                                className="h-5 w-5 accent-emerald-500"
                              />
                            </label>
                          ))}
                        </div>

                        <Button onClick={() => handleSavePlan(planConfig.plan)} disabled={!isSuperAdmin || savingPlan === planConfig.plan} className="mt-5 w-full rounded-2xl">
                          {savingPlan === planConfig.plan ? <RefreshCw className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                          Salvar plano
                        </Button>
                      </article>
                    );
                  })}
                </section>

                {!isSuperAdmin && (
                  <div className="rounded-[1.6rem] border border-amber-100 bg-amber-50 p-4 text-sm font-bold text-amber-800">
                    Apenas o super administrador pode alterar preços, recursos e regras comerciais dos planos.
                  </div>
                )}
              </div>
            ) : activeTab === 'companies' ? (
              <div className="rounded-[2rem] border border-white/70 bg-white shadow-xl shadow-slate-200/70">
                <div className="flex flex-col gap-4 border-b border-slate-100 p-5 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h2 className="text-xl font-black text-slate-950">Empresas cadastradas</h2>
                    <p className="mt-1 text-sm font-medium text-slate-500">
                      Status comercial, vencimento, recursos ativos e ações permitidas por nível.
                    </p>
                  </div>
                  <span className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-black text-slate-600">
                    {companiesList.length} registros
                  </span>
                </div>

                <div className="divide-y divide-slate-100">
                  {companiesList.length === 0 ? (
                    <div className="p-10 text-center">
                      <Building2 className="mx-auto h-12 w-12 text-slate-300" />
                      <p className="mt-4 font-bold text-slate-500">Nenhuma empresa encontrada.</p>
                    </div>
                  ) : (
                    companiesList.map(company => {
                      const days = daysUntil(company.nextBillingDate);
                      const isCanceled = company.subscriptionStatus === SubscriptionStatus.CANCELED;
                      const isPending = company.subscriptionStatus === SubscriptionStatus.PENDING;
                      const hasExpiredBilling = days !== null && days < 0;
                      const isExpired = hasExpiredBilling && (
                        (!isCanceled && !isPending)
                        || String(company.mercadoPagoStatus || '').toLowerCase() === 'expired'
                      );
                      const isPaused = (isCanceled || isPending) && !isExpired;
                      const isClose = days !== null && days >= 0 && days <= 7;
                      const billingTitle = isExpired || isPaused ? 'Situação' : 'Vencimento';
                      const billingMain = isExpired
                        ? 'Conta vencida'
                        : isPaused
                          ? 'Conta pausada'
                          : formatDate(company.nextBillingDate);
                      const billingDescription = isExpired
                        ? 'Assinatura vencida. Renove ou ative com uma nova data.'
                        : isPaused
                          ? 'Não está usando agora. Pode reativar quando voltar.'
                          : days === null
                            ? 'Sem leitura'
                            : `${days} dias restantes`;

                      return (
                        <article key={company.id} className="p-4 transition hover:bg-slate-50/80 sm:p-5">
                          <div className="grid gap-4 xl:grid-cols-[minmax(220px,1fr)_minmax(560px,1.45fr)] xl:items-center">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <h3 className="truncate text-lg font-black text-slate-950">{company.name}</h3>
                                <span className={`rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wider ring-1 ${getStatusClass(company.subscriptionStatus)}`}>
                                  {getStatusLabel(company.subscriptionStatus)}
                                </span>
                                {isSuperAdmin ? (
                                  <select
                                    value={company.plan}
                                    onChange={event => handleChangeCompanyPlan(company, event.target.value as BillingPlanKey)}
                                    disabled={loadingAction}
                                    className="rounded-full border border-slate-200 bg-slate-100 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-slate-600 outline-none transition hover:border-emerald-200 hover:bg-emerald-50 disabled:opacity-60"
                                    title="Alterar plano da empresa"
                                  >
                                    {companyPlanKeys.map(plan => (
                                      <option key={plan} value={plan}>{plan}</option>
                                    ))}
                                  </select>
                                ) : (
                                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-slate-500">
                                    {company.plan}
                                  </span>
                                )}
                                {isSuperAdmin && (
                                  <button
                                    type="button"
                                    onClick={() => handleChangeCompanyPrice(company)}
                                    disabled={loadingAction}
                                    className={`rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wider ring-1 transition disabled:opacity-60 ${
                                      company.monthlyPriceOverride
                                        ? 'bg-emerald-50 text-emerald-700 ring-emerald-200 hover:bg-emerald-100'
                                        : 'bg-white text-slate-500 ring-slate-200 hover:bg-slate-50'
                                    }`}
                                    title={company.monthlyPriceOverride ? 'Preço individual ativo' : 'Definir preço individual'}
                                  >
                                    {company.monthlyPriceOverride ? formatCurrency(getCompanyMonthlyPrice(company)) : 'Preço'}
                                  </button>
                                )}
                                {isSuperAdmin && (
                                  <button
                                    type="button"
                                    onClick={() => handleChangeCompanyInvoiceLimit(company)}
                                    disabled={loadingAction}
                                    className={`rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wider ring-1 transition disabled:opacity-60 ${
                                      company.invoicePhotoOcrMonthlyLimit !== null && company.invoicePhotoOcrMonthlyLimit !== undefined
                                        ? 'bg-cyan-50 text-cyan-700 ring-cyan-200 hover:bg-cyan-100'
                                        : 'bg-white text-slate-500 ring-slate-200 hover:bg-slate-50'
                                    }`}
                                    title="Definir limite individual de leituras por foto"
                                  >
                                    {company.invoicePhotoOcrMonthlyLimit !== null && company.invoicePhotoOcrMonthlyLimit !== undefined
                                      ? `${company.invoicePhotoOcrMonthlyLimit} leituras`
                                      : 'Leituras'}
                                  </button>
                                )}
                                {isSuperAdmin && (
                                  <button
                                    type="button"
                                    onClick={() => handleChangeCompanyBillingEmail(company)}
                                    disabled={loadingAction}
                                    className={`rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wider ring-1 transition disabled:opacity-60 ${
                                      company.billingEmail
                                        ? 'bg-sky-50 text-sky-700 ring-sky-200 hover:bg-sky-100'
                                        : 'bg-white text-slate-500 ring-slate-200 hover:bg-slate-50'
                                    }`}
                                    title={company.billingEmail ? `Cobranca: ${company.billingEmail}` : 'Definir e-mail de Cobranca'}
                                  >
                                    Cobranca
                                  </button>
                                )}
                              </div>
                              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs font-semibold text-slate-500">
                                <span>{company.email}</span>
                                {company.billingEmail && <span>Cobranca: {company.billingEmail}</span>}
                                <span>{company.document || 'Documento não informado'}</span>
                                {company.referredByName && <span>Indicado por {company.referredByName}</span>}
                              </div>
                            </div>

                            <div className="grid gap-3 sm:grid-cols-[minmax(180px,1fr)_minmax(180px,1fr)_172px]">
                              <button
                                onClick={() => {
                                  if (!canManageBilling) return;
                                  setDateEditCompanyId(company.id);
                                  setNewBillingDate(formatDateForInput(company.nextBillingDate));
                                }}
                                disabled={!canManageBilling}
                                className={`min-h-[84px] rounded-2xl border p-3 text-left transition ${
                                  isPaused
                                    ? 'border-slate-200 bg-slate-50'
                                    : isExpired
                                      ? 'border-rose-200 bg-rose-50'
                                      : isClose
                                        ? 'border-amber-200 bg-amber-50'
                                        : 'border-slate-200 bg-white'
                                } ${canManageBilling ? 'hover:border-slate-300' : 'cursor-not-allowed opacity-70'}`}
                              >
                                <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                                  {billingTitle}
                                </p>
                                <p className="mt-1 text-sm font-black text-slate-900">
                                  {billingMain}
                                </p>
                                <p className={`mt-1 max-w-[210px] text-xs font-bold leading-5 ${
                                  isExpired ? 'text-rose-600' : isPaused ? 'text-slate-500' : isClose ? 'text-amber-600' : 'text-slate-400'
                                }`}>
                                  {billingDescription}
                                </p>
                              </button>

                              <div className="min-h-[84px] rounded-2xl border border-slate-200 bg-white p-3">
                                <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Recursos</p>
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                  {company.useStockManagement && <span className="rounded-full bg-emerald-50 px-2 py-1 text-[10px] font-black text-emerald-700">Estoque</span>}
                                  {company.useDelivery && <span className="rounded-full bg-sky-50 px-2 py-1 text-[10px] font-black text-sky-700">Delivery</span>}
                                  {company.useWaiterApp && <span className="rounded-full bg-indigo-50 px-2 py-1 text-[10px] font-black text-indigo-700">Garçom</span>}
                                  {company.useKitchenDisplay && <span className="rounded-full bg-amber-50 px-2 py-1 text-[10px] font-black text-amber-700">Cozinha</span>}
                                  {!company.useStockManagement && !company.useDelivery && !company.useWaiterApp && !company.useKitchenDisplay && (
                                    <span className="text-xs font-bold text-slate-400">Sem módulos extras</span>
                                  )}
                                </div>
                              </div>

                              <div className="flex min-h-[84px] items-center justify-center rounded-2xl border border-slate-200 bg-white p-2">
                                {canManageBilling && (
                                  <div className="flex items-center gap-1 rounded-2xl bg-slate-50 p-1 ring-1 ring-slate-100">
                                    <button
                                      onClick={() => {
                                        setEditingCompanyId(company.id);
                                        setEditingCompanyName(company.name);
                                      }}
                                      className="rounded-xl p-1.5 text-slate-500 transition hover:bg-white hover:text-orange-600 hover:shadow-sm"
                                      title="Resetar senha"
                                    >
                                      <KeyRound className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => handleCreateBillingLink(company)}
                                      disabled={loadingAction}
                                      className="rounded-xl p-1.5 text-slate-500 transition hover:bg-white hover:text-sky-600 hover:shadow-sm disabled:opacity-50"
                                      title={`Gerar cobrança Mercado Pago do plano ${company.plan}`}
                                    >
                                      <WalletCards className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => handleToggleStatus(company)}
                                      disabled={loadingAction}
                                      className={`rounded-xl px-2 py-1.5 shadow-sm ring-1 transition disabled:opacity-50 ${
                                        company.subscriptionStatus === SubscriptionStatus.ACTIVE || company.subscriptionStatus === SubscriptionStatus.TRIAL
                                          ? 'bg-white text-rose-600 ring-rose-100 hover:bg-rose-50'
                                          : 'bg-emerald-600 text-white ring-emerald-500 hover:bg-emerald-700'
                                      }`}
                                      title={company.subscriptionStatus === SubscriptionStatus.ACTIVE || company.subscriptionStatus === SubscriptionStatus.TRIAL ? 'Pausar empresa' : 'Ativar empresa por nova data'}
                                    >
                                      {company.subscriptionStatus === SubscriptionStatus.ACTIVE || company.subscriptionStatus === SubscriptionStatus.TRIAL ? <Ban className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
                                    </button>
                                  </div>
                                )}
                                {canDeleteCompanies && (
                                  <button
                                    onClick={() => handleDeleteCompany(company.id, company.name)}
                                    className="ml-1 rounded-xl p-1.5 text-slate-400 transition hover:bg-rose-50 hover:text-rose-600"
                                    title="Excluir empresa"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                )}
                                {!canManageBilling && (
                                  <span className="text-right text-xs font-bold text-slate-400">
                                    Ações bloqueadas pelo seu nível
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </article>
                      );
                    })
                  )}
                </div>
              </div>
            ) : activeTab === 'admins' ? (
              <div className="space-y-5">
                <div className="rounded-[2rem] border border-white/70 bg-white p-4 shadow-xl shadow-slate-200/70">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                      <p className="text-[11px] font-black uppercase tracking-[0.22em] text-emerald-600">Organograma FlowTab</p>
                      <h2 className="mt-1 text-2xl font-black text-slate-950">Quem trabalha com quem</h2>
                      <p className="mt-1 text-sm font-semibold text-slate-500">A árvore mostra os funcionários internos da empresa. Clique em um funcionário para ver os clientes que ele trouxe ou acompanha.</p>
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                      <input value={teamSearch} onChange={event => setTeamSearch(event.target.value)} placeholder="Pesquisar funcionário ou cliente..." className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold outline-none focus:border-emerald-400" />
                      <Button variant="secondary" size="sm" onClick={() => setTeamPanOffset({ x: 0, y: 0 })} className="rounded-2xl">Centralizar</Button>
                      <Button variant="secondary" size="sm" onClick={() => setShowTeamMessageModal(true)} className="rounded-2xl"><MessageSquareText className="h-4 w-4" />Recado</Button>
                      {canManageAdmins && <Button size="sm" onClick={() => setShowAdminModal(true)}><Plus className="h-4 w-4" />Novo admin</Button>}
                    </div>
                  </div>
                </div>

                <section
                  ref={teamCanvasRef}
                  onMouseDown={handleTeamCanvasMouseDown}
                  onMouseMove={handleTeamCanvasMouseMove}
                  onMouseUp={stopTeamCanvasPan}
                  onMouseLeave={stopTeamCanvasPan}
                  className="min-h-[620px] cursor-grab overflow-hidden rounded-[2.25rem] border border-white/70 bg-[radial-gradient(circle_at_top_left,rgba(16,185,129,0.12),transparent_26rem),linear-gradient(180deg,#ffffff,#f8fafc)] p-5 shadow-xl shadow-slate-200/70 select-none"
                >
                  <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Mapa da equipe</p>
                      <h3 className="text-xl font-black text-slate-950">Chefes fixos na origem, funcionários expandindo por hierarquia</h3>
                    </div>
                    <div className="rounded-full bg-slate-950 px-4 py-2 text-[11px] font-black uppercase tracking-wider text-white">Clique e arraste para navegar</div>
                  </div>

                  <div className="min-w-[980px] pb-8 transition-transform duration-75 ease-out" style={{ transform: `translateX(${teamPanOffset.x}px)` }}>
                    {visibleTeamRoots.length === 0 ? (
                      <div className="rounded-[2rem] border-2 border-dashed border-slate-200 bg-white p-10 text-center">
                        <Users className="mx-auto h-12 w-12 text-slate-300" />
                        <p className="mt-4 font-black text-slate-500">Nenhum funcionário encontrado.</p>
                      </div>
                    ) : (
                      <div className="flex items-start justify-center gap-10 pt-2">
                        {visibleTeamRoots.map(admin => <div key={admin.id}>{renderTeamNode(admin)}</div>)}
                      </div>
                    )}
                  </div>
                </section>

                <section className="rounded-[2rem] border border-white/70 bg-white p-4 shadow-xl shadow-slate-200/70">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h3 className="font-black text-slate-950">Mural interno</h3>
                      <p className="mt-1 text-xs font-semibold text-slate-500">Recados recentes da equipe administrativa.</p>
                    </div>
                    <Button variant="secondary" size="sm" onClick={() => setShowTeamMessageModal(true)} className="rounded-2xl"><MessageSquareText className="h-4 w-4" />Novo recado</Button>
                  </div>
                  <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                    {teamMessages.length === 0 ? <p className="rounded-2xl bg-slate-50 p-4 text-sm font-bold text-slate-400">Nenhum recado ainda.</p> : teamMessages.slice(0, 6).map(message => {
                      const targetAdmin = admins.find(admin => admin.id === message.targetAdminId);
                      const targetCompany = companiesList.find(company => company.id === message.targetCompanyId);
                      return <div key={message.id} className={`rounded-2xl border p-4 ${message.status === 'DONE' ? 'border-slate-100 bg-slate-50 opacity-70' : message.priority === 'URGENT' ? 'border-rose-200 bg-rose-50' : message.priority === 'IMPORTANT' ? 'border-amber-200 bg-amber-50' : 'border-slate-100 bg-white'}`}>
                        <div className="flex items-start justify-between gap-2"><div><p className="text-sm font-black text-slate-950">{message.authorName}</p><p className="text-[11px] font-bold text-slate-500">{new Date(message.createdAt).toLocaleString('pt-BR')}</p></div><button onClick={() => toggleTeamMessageStatus(message)} className="rounded-xl bg-white px-2 py-1 text-[10px] font-black text-slate-600 shadow-sm">{message.status === 'DONE' ? 'Reabrir' : 'Concluir'}</button></div>
                        <p className="mt-3 line-clamp-3 text-sm font-semibold leading-6 text-slate-700">{message.message}</p>
                        <div className="mt-3 flex flex-wrap gap-1.5 text-[10px] font-black uppercase tracking-wider">{targetAdmin && <span className="rounded-full bg-slate-100 px-2 py-1 text-slate-600">{targetAdmin.name}</span>}{targetCompany && <span className="rounded-full bg-emerald-50 px-2 py-1 text-emerald-700">{targetCompany.name}</span>}<span className="rounded-full bg-slate-100 px-2 py-1 text-slate-600">{message.priority}</span></div>
                      </div>;
                    })}
                  </div>
                </section>

                {unassignedCompanies.length > 0 && (
                  <section className="rounded-[2rem] border-2 border-dashed border-amber-200 bg-amber-50 p-4">
                    <h3 className="font-black text-amber-900">Clientes sem responsável definido</h3>
                    <p className="mt-1 text-xs font-bold text-amber-700">Eles não entram na árvore. Vincule a um funcionário para aparecerem no painel dele.</p>
                    <div className="mt-3 grid gap-2 md:grid-cols-3">
                      {unassignedCompanies.map(company => (
                        <div key={company.id} className="rounded-2xl bg-white p-3">
                          <p className="truncate text-sm font-black text-slate-950">{company.name}</p>
                          <p className="truncate text-[11px] font-semibold text-slate-500">{company.email}</p>
                          {canManageTeam && <select value="" onChange={event => assignCompanyOwner(company, event.target.value)} className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-xs font-bold"><option value="">Definir responsável</option>{admins.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select>}
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {selectedTeamAdmin && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm" onClick={() => setSelectedTeamAdminId(null)}>
                    <div className="max-h-[88vh] w-full max-w-3xl overflow-y-auto rounded-[2rem] bg-white p-5 shadow-2xl" onClick={event => event.stopPropagation()}>
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="text-[11px] font-black uppercase tracking-[0.22em] text-emerald-600">Painel do funcionrio</p>
                          <h3 className="mt-1 text-2xl font-black text-slate-950">{selectedTeamAdmin.name}</h3>
                          <p className="text-sm font-semibold text-slate-500">{selectedTeamAdmin.email}</p>
                        </div>
                        <button onClick={() => setSelectedTeamAdminId(null)} className="rounded-2xl bg-slate-100 p-3 text-slate-500 transition hover:bg-slate-200"><X className="h-5 w-5" /></button>
                      </div>
                      <div className="mt-5 grid gap-3 sm:grid-cols-3">
                        <div className="rounded-2xl bg-slate-50 p-4"><p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Cargo</p><p className="mt-2 font-black text-slate-950">{selectedTeamAdmin.position || getAdminLabel(selectedTeamAdmin.adminLevel)}</p></div>
                        <div className="rounded-2xl bg-slate-50 p-4"><p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Contato</p><p className="mt-2 font-black text-slate-950">{selectedTeamAdmin.phone || 'No informado'}</p></div>
                        <div className="rounded-2xl bg-slate-50 p-4"><p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Clientes</p><p className="mt-2 text-2xl font-black text-slate-950">{selectedTeamAdminClients.length}</p></div>
                      </div>
                      <div className="mt-5 flex flex-wrap gap-2">
                        {canManageTeam && <Button variant="secondary" size="sm" onClick={() => editAdminContact(selectedTeamAdmin)}>Editar contato</Button>}
                        <Button variant="secondary" size="sm" onClick={() => { setTeamTargetAdminId(selectedTeamAdmin.id); setShowTeamMessageModal(true); }}>Enviar recado</Button>
                        {canManageAdmins && selectedTeamAdmin.adminLevel !== 0 && <Button variant="secondary" size="sm" onClick={() => handleToggleAdminEmailPermission(selectedTeamAdmin)}>{selectedTeamAdmin.canSendEmails ? 'Bloquear e-mails' : 'Liberar e-mails'}</Button>}
                        {canManageAdmins && selectedTeamAdmin.adminLevel !== 0 && <Button variant="secondary" size="sm" onClick={() => updateAdminTeamInfo(selectedTeamAdmin, { canLeadTeam: !selectedTeamAdmin.canLeadTeam, supervisorId: selectedTeamAdmin.canLeadTeam ? '' : selectedTeamAdmin.supervisorId, supervisorName: selectedTeamAdmin.canLeadTeam ? '' : selectedTeamAdmin.supervisorName })}>{selectedTeamAdmin.canLeadTeam ? 'Remover como chefe' : 'Liberar como chefe'}</Button>}
                        {canManageAdmins && user?.id !== selectedTeamAdmin.id && <Button variant="secondary" size="sm" onClick={() => handleDeleteAdmin(selectedTeamAdmin)}>Remover admin</Button>}
                      </div>
                      <div className="mt-6">
                        <h4 className="font-black text-slate-950">Clientes vinculados</h4>
                        <div className="mt-3 grid gap-3 sm:grid-cols-2">
                          {selectedTeamAdminClients.length === 0 ? <p className="rounded-2xl bg-slate-50 p-4 text-sm font-bold text-slate-400">Nenhum cliente vinculado a este funcionário.</p> : selectedTeamAdminClients.map(company => (
                            <div key={company.id} className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0"><p className="truncate font-black text-slate-950">{company.name}</p><p className="truncate text-xs font-semibold text-slate-500">{company.email}</p>{company.contactPhone && <p className="mt-1 text-xs font-bold text-slate-500">{company.contactPhone}</p>}</div>
                                <span className={`shrink-0 rounded-full px-2 py-1 text-[9px] font-black uppercase ring-1 ${getStatusClass(company.subscriptionStatus)}`}>{getStatusLabel(company.subscriptionStatus)}</span>
                              </div>
                              {canManageTeam && <div className="mt-3 flex gap-2"><button onClick={() => editCompanyContact(company)} className="rounded-xl bg-white px-3 py-2 text-[10px] font-black text-slate-600 shadow-sm">Contato</button><select value={getCompanyOwnerAdminId(company)} onChange={event => assignCompanyOwner(company, event.target.value)} className="min-w-0 flex-1 rounded-xl border border-slate-200 bg-white px-2 py-2 text-[10px] font-bold outline-none"><option value="">Responsvel</option>{admins.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></div>}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-5">
                {isSuperAdmin && (
                  <section className="rounded-[2rem] border border-emerald-100 bg-emerald-50/70 p-5 shadow-xl shadow-slate-200/50">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                      <div className="flex items-start gap-3">
                        <div className="rounded-2xl bg-white p-3 text-emerald-600 shadow-sm"><Clock className="h-5 w-5" /></div>
                        <div>
                          <p className="text-[11px] font-black uppercase tracking-[0.22em] text-emerald-700">Super Admin</p>
                          <h2 className="mt-1 text-xl font-black text-slate-950">Recorrência dos avisos de assinatura</h2>
                          <p className="mt-1 max-w-2xl text-sm font-semibold text-slate-600">Evite mensagens diárias e escolha quando os clientes podem receber lembretes automáticos.</p>
                        </div>
                      </div>
                      <div className="w-full lg:max-w-sm">
                        <select
                          value={billingConfig?.subscriptionNoticeRecurrence || 'PROGRESSIVE_AFTER_EXPIRY'}
                          onChange={event => handleNoticeRecurrenceChange(event.target.value as SubscriptionNoticeRecurrence)}
                          disabled={savingNoticeRecurrence}
                          className="w-full rounded-2xl border border-emerald-200 bg-white p-3 text-sm font-black text-slate-800 outline-none focus:border-emerald-500 disabled:opacity-60"
                        >
                          {subscriptionNoticeRecurrenceOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </select>
                        <p className="mt-2 text-xs font-semibold text-slate-500">{subscriptionNoticeRecurrenceOptions.find(option => option.value === (billingConfig?.subscriptionNoticeRecurrence || 'PROGRESSIVE_AFTER_EXPIRY'))?.description}</p>
                      </div>
                    </div>
                  </section>
                )}
                <div className="rounded-[2rem] border border-white/70 bg-white p-5 shadow-xl shadow-slate-200/70">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-[11px] font-black uppercase tracking-[0.22em] text-emerald-600">Central de comunicação</p>
                      <h2 className="mt-1 text-2xl font-black text-slate-950">E-mails FlowTab</h2>
                      <p className="mt-1 text-sm font-semibold text-slate-500">Envie comunicados, teste modelos e chame clientes pausados de volta.</p>
                    </div>
                    <span className="rounded-2xl bg-slate-950 px-4 py-3 text-xs font-black uppercase tracking-wider text-white">{selectedEmailCompanyIds.length} selecionado(s)</span>
                  </div>
                </div>

                <div className="grid gap-5 xl:grid-cols-[320px_1fr]">
                  <aside className="rounded-[2rem] border border-white/70 bg-white p-4 shadow-xl shadow-slate-200/70">
                    <div className="mb-4 flex items-center justify-between gap-2"><div><h3 className="font-black text-slate-950">Clientes</h3><p className="text-xs font-semibold text-slate-500">Escolha quem vai receber.</p></div><button onClick={selectPausedCompanies} className="rounded-xl bg-amber-50 px-3 py-2 text-[11px] font-black text-amber-700">Pausados</button></div>
                    <div className="max-h-[520px] space-y-2 overflow-y-auto pr-1">
                      {companiesList.map(company => (
                        <label key={company.id} className={`flex cursor-pointer items-center gap-3 rounded-2xl border p-3 transition ${selectedEmailCompanyIds.includes(company.id) ? 'border-emerald-200 bg-emerald-50' : 'border-slate-100 bg-slate-50 hover:bg-white'}`}>
                          <input type="checkbox" checked={selectedEmailCompanyIds.includes(company.id)} onChange={() => toggleEmailCompany(company.id)} className="h-4 w-4 accent-emerald-600" />
                          <div className="min-w-0"><p className="truncate text-sm font-black text-slate-900">{company.name}</p><p className="truncate text-xs font-semibold text-slate-500">{company.email}</p></div>
                        </label>
                      ))}
                    </div>
                  </aside>
                  <section className="grid gap-5 lg:grid-cols-2">
                    <div className="rounded-[2rem] border border-white/70 bg-white p-5 shadow-xl shadow-slate-200/70 lg:col-span-2">
                      <div className="mb-4 flex items-center gap-3">
                        <div className="rounded-2xl bg-slate-950 p-3 text-white"><Send className="h-5 w-5" /></div>
                        <div>
                          <h3 className="font-black text-slate-950">Enviar e-mail para clientes</h3>
                          <p className="text-sm font-semibold text-slate-500">Use <strong>@empresa</strong> para personalizar automaticamente cada mensagem.</p>
                        </div>
                      </div>
                      <div className="mb-3 rounded-2xl border border-emerald-100 bg-emerald-50 p-3 text-xs font-bold text-emerald-700">
                        Exemplo: "Olá @empresa, temos uma novidade para você." Cada cliente recebe com o próprio nome da empresa.
                      </div>
                      <div className="grid gap-3 md:grid-cols-2">
                        <input value={emailSubject} onChange={event => setEmailSubject(event.target.value)} placeholder="Assunto do e-mail" className="rounded-2xl border border-slate-200 bg-slate-50 p-3 text-sm font-bold outline-none focus:border-emerald-400 md:col-span-2" />
                        {renderCompanyVariableField('Mensagem principal', emailMessage, setEmailMessage, 'Digite a mensagem. Use @empresa para personalizar.', 5)}
                        {renderCompanyVariableField('Frase extra opcional', emailExtraMessage, setEmailExtraMessage, 'Digite uma frase extra. Use @empresa se quiser.', 5)}
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                          <label className="mb-2 block text-[10px] font-black uppercase tracking-widest text-slate-400">Botão do e-mail</label>
                          <input value={emailCtaLabel} onChange={event => setEmailCtaLabel(event.target.value)} placeholder="Texto do botão" className="w-full bg-transparent text-sm font-bold text-slate-800 outline-none placeholder:text-slate-400" />
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                          <label className="mb-2 block text-[10px] font-black uppercase tracking-widest text-slate-400">Link seguro</label>
                          <input value={emailCtaUrl} onChange={event => setEmailCtaUrl(event.target.value)} placeholder="https://www.flowtab.com.br/" className="w-full bg-transparent text-sm font-bold text-slate-800 outline-none placeholder:text-slate-400" />
                        </div>
                      </div>
                      <div className="mt-3 rounded-2xl border border-slate-100 bg-slate-50 p-3 text-xs font-bold text-slate-500">
                        Editor seguro: não aceita HTML livre. O FlowTab monta o layout profissional e libera apenas texto, @empresa e botão com link oficial.
                      </div>
                      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                        <Button variant="secondary" onClick={handlePreviewCustomEmail} disabled={sendingEmailCenter} className="rounded-2xl"><Mail className="h-4 w-4" />Prévia para mim</Button>
                        <Button onClick={handleSendCustomEmail} disabled={sendingEmailCenter} className="rounded-2xl"><Send className="h-4 w-4" />Enviar comunicado</Button>
                      </div>
                      <p className="mt-3 text-xs font-semibold text-slate-400">Prévia vai somente para {user?.email || 'o e-mail do admin logado'}. O envio real usa apenas as empresas selecionadas no banco.</p>
                    </div>

                    <div className="rounded-[2rem] border border-white/70 bg-white p-5 shadow-xl shadow-slate-200/70">
                      <div className="mb-4 flex items-center gap-3">
                        <div className="rounded-2xl bg-emerald-50 p-3 text-emerald-600"><Mail className="h-5 w-5" /></div>
                        <div><h3 className="font-black text-slate-950">Modelos automáticos</h3><p className="text-sm font-semibold text-slate-500">Teste os e-mails do sistema sem enviar para clientes.</p></div>
                      </div>
                      <div className="space-y-3">
                        <div className="rounded-2xl border border-slate-100 bg-slate-50 p-3 text-xs font-bold text-slate-600">A prévia ser? enviada somente para <span className="text-slate-950">{user?.email || 'o admin logado'}</span>.</div>
                        <select value={selectedPreviewType} onChange={event => setSelectedPreviewType(event.target.value as CommunicationPreviewType)} className="w-full rounded-2xl border border-slate-200 bg-slate-50 p-3 text-sm font-bold outline-none focus:border-emerald-400">
                          {communicationPreviewOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </select>
                        <Button variant="secondary" onClick={handleSendPreviewEmails} disabled={sendingPreviewEmails} className="w-full rounded-2xl"><Mail className="h-4 w-4" />Enviar prévia para mim</Button>
                      </div>
                    </div>

                    <div className="rounded-[2rem] border border-white/70 bg-white p-5 shadow-xl shadow-slate-200/70">
                      <div className="mb-4 flex items-center gap-3">
                        <div className="rounded-2xl bg-amber-50 p-3 text-amber-600"><MessageSquareText className="h-5 w-5" /></div>
                        <div><h3 className="font-black text-slate-950">Chamar para voltar</h3><p className="text-sm font-semibold text-slate-500">Para clientes pausados ou sem uso.</p></div>
                      </div>
                      {renderCompanyVariableField('Frase de retorno', winbackExtraMessage, setWinbackExtraMessage, 'Condição especial, contato ou recado do suporte. Use @empresa se quiser.', 6)}
                      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                        <Button variant="secondary" onClick={handlePreviewWinbackEmail} disabled={sendingEmailCenter} className="w-full rounded-2xl"><Mail className="h-4 w-4" />Prévia para mim</Button>
                        <Button onClick={handleSendWinbackEmail} disabled={sendingEmailCenter} className="w-full rounded-2xl"><Sparkles className="h-4 w-4" />Enviar convite</Button>
                      </div>
                    </div>
                  </section>
                </div>
              </div>
            )}
          </section>

          {false && activeTab === 'dashboard' && (
          <aside className="space-y-5">
            <section className="rounded-[2rem] border border-white/70 bg-white p-5 shadow-xl shadow-slate-200/70">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg font-black text-slate-950">Convites</h2>
                  <p className="mt-1 text-sm font-medium text-slate-500">
                    Códigos ativos para novos cadastros.
                  </p>
                </div>
                <div className="rounded-2xl bg-emerald-50 p-3 text-emerald-600">
                  <Ticket className="h-5 w-5" />
                </div>
              </div>

              <Button
                onClick={handleGenerateCode}
                disabled={(activeInvites || []).length >= 5 || generatingCode}
                className="mt-5 w-full rounded-2xl"
              >
                {generatingCode ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                Gerar código
              </Button>

              <div className="mt-5 space-y-3">
                {(activeInvites || []).length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-5 text-center text-sm font-bold text-slate-400">
                    Nenhum código ativo.
                  </div>
                ) : (
                  activeInvites.map(invite => (
                    <div key={invite.id} className="rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="font-mono text-2xl font-black tracking-widest text-emerald-700">{invite.code}</p>
                          <p className="mt-1 flex items-center gap-1 text-xs font-bold text-emerald-600">
                            <Clock className="h-3 w-3" />
                            Expira em 7 dias
                          </p>
                          {invite.generatedByName && (
                            <p className="mt-1 text-[11px] font-semibold text-slate-400">
                              Gerado por {invite.generatedByName}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={() => copyInvite(invite.code)}
                            className="rounded-xl bg-white p-2 text-slate-500 shadow-sm transition hover:text-emerald-600"
                            title="Copiar código"
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteInvite(invite.id)}
                            className="rounded-xl bg-white p-2 text-rose-500 shadow-sm transition hover:bg-rose-50"
                            title="Excluir código"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>

            <section className="rounded-[2rem] border border-white/70 bg-white p-5 shadow-xl shadow-slate-200/70">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-black text-slate-950">Saúde da base</h2>
                  <p className="mt-1 text-sm font-medium text-slate-500">Resumo operacional dos clientes.</p>
                </div>
                <Activity className="h-5 w-5 text-slate-400" />
              </div>

              <div className="mt-5 space-y-3">
                <div className="flex items-center justify-between rounded-2xl bg-slate-50 p-4">
                  <span className="font-bold text-slate-600">Com estoque ativo</span>
                  <strong className="text-slate-950">{metrics.enabledStock}</strong>
                </div>
                <div className="flex items-center justify-between rounded-2xl bg-slate-50 p-4">
                  <span className="font-bold text-slate-600">Com delivery ativo</span>
                  <strong className="text-slate-950">{metrics.enabledDelivery}</strong>
                </div>
                <div className="flex items-center justify-between rounded-2xl bg-slate-50 p-4">
                  <span className="font-bold text-slate-600">Vencendo em 7 dias</span>
                  <strong className="text-slate-950">{metrics.expiringSoon.length}</strong>
                </div>
              </div>
            </section>

            <section className="rounded-[2rem] border border-white/70 bg-slate-950 p-5 text-white shadow-xl shadow-slate-200/70">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-black">Planos</h2>
                  <p className="mt-1 text-sm font-medium text-slate-300">Distribuição comercial.</p>
                </div>
                <ArrowUpRight className="h-5 w-5 text-emerald-300" />
              </div>

              <div className="mt-5 space-y-3">
                {(['BASIC', 'PRO', 'ENTERPRISE'] as Company['plan'][]).map(plan => (
                  <div key={plan} className="flex items-center justify-between rounded-2xl bg-white/10 p-4 ring-1 ring-white/10">
                    <span className="font-black">{plan}</span>
                    <span className="rounded-full bg-white px-2.5 py-1 text-xs font-black text-slate-950">{planCounts[plan] || 0}</span>
                  </div>
                ))}
              </div>
            </section>
          </aside>
          )}
        </div>
      </main>

      {showTeamMessageModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-[2rem] bg-white p-6 shadow-2xl">
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.22em] text-emerald-600">Recado interno</p>
                <h2 className="mt-1 text-2xl font-black text-slate-950">Mensagem para equipe</h2>
                <p className="mt-1 text-sm font-semibold text-slate-500">Envie para todos, para a equipe de um chefe ou para alguém específico.</p>
              </div>
              <button onClick={() => setShowTeamMessageModal(false)} className="rounded-2xl bg-slate-100 p-2 text-slate-500 transition hover:bg-slate-200">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3">
              <select value={teamTargetAdminId} onChange={event => setTeamTargetAdminId(event.target.value)} className="w-full rounded-2xl border border-slate-200 bg-slate-50 p-3 text-sm font-bold outline-none focus:border-emerald-400">
                <option value="">Toda equipe administrativa</option>
                {admins.map(admin => <option key={admin.id} value={admin.id}>Específico: {admin.name}</option>)}
              </select>
              <select
                value={teamTargetAdminId.startsWith('team:') ? teamTargetAdminId : ''}
                onChange={event => setTeamTargetAdminId(event.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 p-3 text-sm font-bold outline-none focus:border-emerald-400"
              >
                <option value="">Ou selecione uma equipe por chefe</option>
                {admins.map(admin => <option key={admin.id} value={`team:${admin.id}`}>Equipe de {admin.name}</option>)}
              </select>
              <select value={teamTargetCompanyId} onChange={event => setTeamTargetCompanyId(event.target.value)} className="w-full rounded-2xl border border-slate-200 bg-slate-50 p-3 text-sm font-bold outline-none focus:border-emerald-400">
                <option value="">Sem cliente específico</option>
                {companiesList.map(company => <option key={company.id} value={company.id}>{company.name}</option>)}
              </select>
              <select value={teamPriority} onChange={event => setTeamPriority(event.target.value as 'NORMAL' | 'IMPORTANT' | 'URGENT')} className="w-full rounded-2xl border border-slate-200 bg-slate-50 p-3 text-sm font-bold outline-none focus:border-emerald-400">
                <option value="NORMAL">Normal</option>
                <option value="IMPORTANT">Importante</option>
                <option value="URGENT">Urgente</option>
              </select>
              <textarea value={teamMessage} onChange={event => setTeamMessage(event.target.value)} rows={6} placeholder="Escreva o recado interno..." className="w-full rounded-2xl border border-slate-200 bg-slate-50 p-3 text-sm font-bold outline-none focus:border-emerald-400" />
              <Button onClick={handleSendTeamMessage} disabled={savingTeam} className="w-full rounded-2xl">
                <MessageSquareText className="h-4 w-4" />
                Enviar recado
              </Button>
            </div>
          </div>
        </div>
      )}

      {dateEditCompanyId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-[2rem] bg-white p-7 shadow-2xl">
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-black text-slate-950">Alterar vencimento</h2>
                <p className="mt-1 text-sm font-medium text-slate-500">Atualize a data de cobrança do cliente.</p>
              </div>
              <button onClick={() => setDateEditCompanyId(null)} className="rounded-xl bg-slate-100 p-2 text-slate-400">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleUpdateBillingDate} className="space-y-4">
              <div>
                <label className="mb-1 block text-[10px] font-black uppercase tracking-widest text-slate-400">Nova data</label>
                <input
                  required
                  type="date"
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 p-4 font-bold text-slate-800 outline-none focus:border-emerald-400"
                  value={newBillingDate}
                  onChange={event => setNewBillingDate(event.target.value)}
                />
              </div>
              <Button type="submit" disabled={loadingAction} className="h-13 w-full rounded-2xl">
                {loadingAction ? <RefreshCw className="animate-spin" /> : 'Atualizar vencimento'}
              </Button>
            </form>
          </div>
        </div>
      )}

      {editingCompanyId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-[2rem] bg-white p-7 shadow-2xl">
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-black text-slate-950">Resetar senha</h2>
                <p className="mt-1 text-sm font-medium text-slate-500">{editingCompanyName}</p>
              </div>
              <button onClick={() => setEditingCompanyId(null)} className="rounded-xl bg-slate-100 p-2 text-slate-400">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleUpdatePassword} className="space-y-4">
              <div>
                <label className="mb-1 block text-[10px] font-black uppercase tracking-widest text-slate-400">Nova senha</label>
                <input
                  required
                  type="password"
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 p-4 text-lg font-bold text-slate-800 outline-none focus:border-emerald-400"
                  value={newPassword}
                  onChange={event => setNewPassword(event.target.value)}
                  placeholder="âââ€š¬¢âââ€š¬¢âââ€š¬¢âââ€š¬¢âââ€š¬¢âââ€š¬¢"
                />
              </div>
              <Button type="submit" disabled={loadingAction} className="h-13 w-full rounded-2xl">
                {loadingAction ? <RefreshCw className="animate-spin" /> : 'Confirmar novo acesso'}
              </Button>
            </form>
          </div>
        </div>
      )}

      {showAdminModal && canManageAdmins && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-[2rem] bg-white p-7 shadow-2xl">
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-black text-slate-950">Novo administrador</h2>
                <p className="mt-1 text-sm font-medium text-slate-500">Defina nome, acesso e nível de permissão.</p>
              </div>
              <button onClick={() => setShowAdminModal(false)} className="rounded-xl bg-slate-100 p-2 text-slate-400">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleCreateAdmin} className="space-y-4">
              <div>
                <label className="mb-1 block text-[10px] font-black uppercase tracking-widest text-slate-400">Nome</label>
                <input
                  required
                  type="text"
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 p-3 font-bold outline-none focus:border-emerald-400"
                  value={newAdminName}
                  onChange={event => setNewAdminName(event.target.value)}
                />
              </div>
              <div>
                <label className="mb-1 block text-[10px] font-black uppercase tracking-widest text-slate-400">Email</label>
                <input
                  required
                  type="email"
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 p-3 font-bold outline-none focus:border-emerald-400"
                  value={newAdminEmail}
                  onChange={event => setNewAdminEmail(event.target.value)}
                />
              </div>
              <div>
                <label className="mb-1 block text-[10px] font-black uppercase tracking-widest text-slate-400">Senha inicial</label>
                <input
                  required
                  type="password"
                  placeholder="âââ€š¬¢âââ€š¬¢âââ€š¬¢âââ€š¬¢âââ€š¬¢âââ€š¬¢"
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 p-3 font-bold outline-none focus:border-emerald-400"
                  value={newAdminPass}
                  onChange={event => setNewAdminPass(event.target.value)}
                />
              </div>
              <div>
                <label className="mb-1 block text-[10px] font-black uppercase tracking-widest text-slate-400">Nível de acesso</label>
                <select
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 p-3 font-bold outline-none focus:border-emerald-400"
                  value={newAdminLevel}
                  onChange={event => setNewAdminLevel(Number(event.target.value) as 0 | 1 | 2)}
                >
                  <option value={2}>Aprovador - básico</option>
                  <option value={1}>Gerente - intermediário</option>
                  <option value={0}>Super Admin - total</option>
                </select>
              </div>
              <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
                <input
                  type="checkbox"
                  checked={newAdminCanSendEmails || newAdminLevel === 0}
                  disabled={newAdminLevel === 0}
                  onChange={event => setNewAdminCanSendEmails(event.target.checked)}
                  className="mt-1 h-4 w-4 accent-emerald-600"
                />
                <span>
                  <span className="block text-sm font-black text-slate-900">Pode enviar e-mails</span>
                  <span className="mt-1 block text-xs font-semibold text-slate-500">
                    Libera a aba E-mails, prévias e campanhas de retorno. Super Admin sempre tem acesso.
                  </span>
                </span>
              </label>
              <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-sky-100 bg-sky-50 p-4">
                <input
                  type="checkbox"
                  checked={newAdminCanLeadTeam || newAdminLevel === 0}
                  disabled={newAdminLevel === 0}
                  onChange={event => setNewAdminCanLeadTeam(event.target.checked)}
                  className="mt-1 h-4 w-4 accent-sky-600"
                />
                <span>
                  <span className="block text-sm font-black text-slate-900">Pode ser chefe</span>
                  <span className="mt-1 block text-xs font-semibold text-slate-500">
                    Permite que outros funcionários respondam a este admin no organograma. Super Admin sempre pode.
                  </span>
                </span>
              </label>
              <Button type="submit" disabled={loadingAction} className="h-12 w-full rounded-2xl">
                {loadingAction ? <RefreshCw className="animate-spin" /> : 'Criar administrador'}
              </Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
